/**
 * An Event is a structured record of a scheduled gathering, including its title, location, date and time, registration settings, status, and related URLs.
 *
 * You can use it to model and display event details, configure how guests register, and manage visibility and calendars across the site or app.
 */
export interface Event {
  /**
   * Event ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /** Event location. */
  location?: Location;
  /** Event date and time settings. */
  dateAndTimeSettings?: DateAndTimeSettings;
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   */
  title?: string | null;
  /**
   * Short description that appears under the event title.
   * **Note:** This field is returned only when the `DETAILS` fieldset is included in the request.
   * @maxLength 500
   */
  shortDescription?: string | null;
  /**
   * Detailed description of an event.
   *
   * This field is deprecated.
   * @maxLength 50000
   * @deprecated Detailed description of an event.
   *
   * This field is deprecated.
   * @replacedBy description
   * @targetRemovalDate 2025-04-15
   */
  detailedDescription?: string | null;
  /**
   * Main event image. <br>
   * **Note:** This field is returned only when the `DETAILS` fieldset is included in the request.
   */
  mainImage?: Image;
  /**
   * Unique identifier of the event page. The naming is the same as the event title written in kebab case. For example, if your event title is "Leather Crafting 101", then the slug is "leather-crafting-101".
   * @readonly
   */
  slug?: string;
  /**
   * Date and time when the event was created.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Date and time when the event was updated.
   * @readonly
   */
  updatedDate?: Date | null;
  /**
   * Event status.
   * @readonly
   */
  status?: StatusWithLiterals;
  /**
   * RSVP or ticketing registration details. <br>
   * **Note:** This field is returned only when the `REGISTRATION` fieldset is included in the request.
   */
  registration?: Registration;
  /**
   * URLs that allow you to add an event to the Google calendar, or to download an [ICS calendar](https://icscalendar.com/) file. <br>
   * **Note:** This field is returned only when the `DETAILS` fieldset is included in the request.
   * @readonly
   */
  calendarUrls?: CalendarUrls;
  /**
   * Event page URL components. <br>
   * **Note:** This field is returned only when the `URLS` fieldset is included in the request.
   */
  eventPageUrl?: PageUrl;
  /**
   * Event registration form. <br>
   * **Note:** This field is returned only when the `FORM` fieldset is included in the request.
   */
  form?: Form;
  /**
   * Summary of RSVP or ticket sales. <br>
   * **Note:** This field is returned only when the `DASHBOARD` fieldset is included in the request and you have the "WIX_EVENTS.READ_EVENT_DASHBOARD" permissions.
   */
  summaries?: Summaries;
  /**
   * Instance ID of the site where the event is hosted.
   * @readonly
   */
  instanceId?: string;
  /** Guest list configuration. */
  guestListSettings?: GuestListSettings;
  /**
   * ID of the user who created the event.
   * @readonly
   * @format GUID
   */
  userId?: string;
  /**
   * Online conferencing details. <br>
   * **Note:** This field is returned only when the `ONLINE_CONFERENCING_SESSION` fieldset is included in the request and you have the "WIX_EVENTS.READ_ONLINE_CONFERENCING" permissions.
   */
  onlineConferencing?: OnlineConferencing;
  /**
   * SEO settings. <br>
   * **Note:** This field is returned only when the `SEO_SETTINGS` fieldset is included in the request.
   */
  seoSettings?: SeoSettings;
  /**
   * Assigned contacts label key.
   * @readonly
   */
  contactLabel?: string | null;
  /**
   * Event schedule details. <br>
   * **Note:** This field is returned only when the `AGENDA` fieldset is included in the request.
   */
  agendaSettings?: AgendaSettings;
  /** Visual settings for event. */
  eventDisplaySettings?: EventDisplaySettings;
  /** Event description. <widget src="https://apps.wix.com/_serverless/ricos-playground-services/goto/api-component" plugins="emoji,audio,codeBlock,collapsibleList,divider,emoji,file,gallery,giphy,hashtag,heading,html,image,indent,lineSpacing,link,linkPreview,spoiler,video" exampleid="9b569a8d-0fc1-40f4-987f-8bd40ecc72d0">Fallback Message for SSR and Error</widget> */
  description?: RichContent;
  /**
   * Event publish timestamp.
   * @readonly
   */
  publishedDate?: Date | null;
}

export interface Location {
  /**
   * Location name. This value is displayed instead of the address when the location is defined as TBD by setting the `locationTbd` property to `true`.
   * @maxLength 50
   */
  name?: string | null;
  /** Location type. */
  type?: LocationTypeWithLiterals;
  /** Exact location address. */
  address?: Address;
  /** Whether the event location is TBD. */
  locationTbd?: boolean | null;
}

export enum LocationType {
  /** Event is on-site at a specific physical location. */
  VENUE = 'VENUE',
  /** Event is online, such as a virtual video conference. */
  ONLINE = 'ONLINE',
}

/** @enumType */
export type LocationTypeWithLiterals = LocationType | 'VENUE' | 'ONLINE';

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Primary address information (street and building number).
   * @maxLength 250
   */
  addressLine?: string | null;
  /**
   * 2-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2).
   * @maxLength 20
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 100
   */
  city?: string | null;
  /**
   * Zip or postal code.
   * @maxLength 100
   */
  postalCode?: string | null;
  /**
   * Secondary address information (suite or apartment number and room number).
   * @maxLength 250
   */
  addressLine2?: string | null;
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street address. */
  streetAddress?: StreetAddress;
  /**
   * Primary address information (street and building number).
   * @maxLength 250
   */
  addressLine?: string | null;
}

export interface StreetAddress {
  /**
   * Street number.
   * @maxLength 100
   */
  number?: string;
  /**
   * Street name.
   * @maxLength 250
   */
  name?: string;
}

export interface AddressLocation {
  /**
   * Address latitude coordinates.
   * @min -90
   * @max 90
   */
  latitude?: number | null;
  /**
   * Address longitude coordinates.
   * @min -180
   * @max 180
   */
  longitude?: number | null;
}

export interface Subdivision {
  /**
   * Short subdivision code.
   * @maxLength 100
   */
  code?: string;
  /**
   * Subdivision full name.
   * @maxLength 250
   */
  name?: string;
}

export enum SubdivisionType {
  UNKNOWN_SUBDIVISION_TYPE = 'UNKNOWN_SUBDIVISION_TYPE',
  /** State */
  ADMINISTRATIVE_AREA_LEVEL_1 = 'ADMINISTRATIVE_AREA_LEVEL_1',
  /** County */
  ADMINISTRATIVE_AREA_LEVEL_2 = 'ADMINISTRATIVE_AREA_LEVEL_2',
  /** City/town */
  ADMINISTRATIVE_AREA_LEVEL_3 = 'ADMINISTRATIVE_AREA_LEVEL_3',
  /** Neighborhood/quarter */
  ADMINISTRATIVE_AREA_LEVEL_4 = 'ADMINISTRATIVE_AREA_LEVEL_4',
  /** Street/block */
  ADMINISTRATIVE_AREA_LEVEL_5 = 'ADMINISTRATIVE_AREA_LEVEL_5',
  /** ADMINISTRATIVE_AREA_LEVEL_0. Indicates the national political entity, and is typically the highest order type returned by the Geocoder. */
  COUNTRY = 'COUNTRY',
}

/** @enumType */
export type SubdivisionTypeWithLiterals =
  | SubdivisionType
  | 'UNKNOWN_SUBDIVISION_TYPE'
  | 'ADMINISTRATIVE_AREA_LEVEL_1'
  | 'ADMINISTRATIVE_AREA_LEVEL_2'
  | 'ADMINISTRATIVE_AREA_LEVEL_3'
  | 'ADMINISTRATIVE_AREA_LEVEL_4'
  | 'ADMINISTRATIVE_AREA_LEVEL_5'
  | 'COUNTRY';

export interface DateAndTimeSettings {
  /** Whether the event date and time are TBD. */
  dateAndTimeTbd?: boolean | null;
  /**
   * Message that is displayed when time and date is TBD.
   *
   * **Note:** This field is only used when the `dateAndTimeTbd` field value is `true`.
   * @maxLength 100
   */
  dateAndTimeTbdMessage?: string | null;
  /**
   * Event start date.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   */
  startDate?: Date | null;
  /**
   * Event end date.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   */
  endDate?: Date | null;
  /**
   * Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.
   *
   * **Note:** This field is only returned when the `dateAndTimeTbd` field value is `false`.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether the end date is hidden in the formatted date and time. */
  hideEndDate?: boolean | null;
  /** Whether the time zone is displayed in the formatted schedule. */
  showTimeZone?: boolean | null;
  /**
   * Repeating event status.
   * @readonly
   */
  recurrenceStatus?: RecurrenceStatusStatusWithLiterals;
  /** Event repetitions. */
  recurringEvents?: Recurrences;
  /** Formatted date and time settings. */
  formatted?: Formatted;
}

export enum RecurrenceStatusStatus {
  /** Event happens only once and can last multiple days. */
  ONE_TIME = 'ONE_TIME',
  /** A series of events that repeat. */
  RECURRING = 'RECURRING',
  /** Next event in a schedule of recurring events. */
  RECURRING_UPCOMING = 'RECURRING_UPCOMING',
  /** Latest event that ended in a schedule of recurring events. */
  RECURRING_RECENTLY_ENDED = 'RECURRING_RECENTLY_ENDED',
  /** Latest canceled event in a schedule of recurring events */
  RECURRING_RECENTLY_CANCELED = 'RECURRING_RECENTLY_CANCELED',
}

/** @enumType */
export type RecurrenceStatusStatusWithLiterals =
  | RecurrenceStatusStatus
  | 'ONE_TIME'
  | 'RECURRING'
  | 'RECURRING_UPCOMING'
  | 'RECURRING_RECENTLY_ENDED'
  | 'RECURRING_RECENTLY_CANCELED';

export interface Recurrences {
  /**
   * Individual event dates for recurring events.
   *
   * *Note:** Each date must be manually calculated and provided. There is no support for automatic generation using recurrence rules or patterns (for example, "Weekly").
   *
   * When you create a recurring event:
   * - Each occurrence is created as an independent event with its own unique ID.
   * - All occurrences in the series share the same `categoryId`, which allows you to identify all events in the recurring series.
   * - To retrieve all events in a recurring series, query events by `recurringEvents.categoryId`.
   * - Each event in the series can be independently updated or deleted using its individual event ID.
   * @maxSize 1000
   */
  individualEventDates?: Occurrence[];
  /**
   * Recurring event category ID.
   *
   * This read-only field is automatically generated and shared by all events in a recurring series. Use this ID to query and retrieve all occurrences of a recurring event.
   * @readonly
   * @maxLength 100
   */
  categoryId?: string | null;
}

export interface Occurrence {
  /** Event start date. */
  startDate?: Date | null;
  /** Event end date. */
  endDate?: Date | null;
  /**
   * Event time zone ID in the [TZ database](https://www.iana.org/time-zones) format.
   * @maxLength 100
   */
  timeZoneId?: string | null;
  /** Whether the time zone is displayed in a formatted schedule. */
  showTimeZone?: boolean;
}

export interface Formatted {
  /**
   * Formatted date and time representation. <br>
   * Example of formatting when an event lasts multiple days and is in the UTC time zone: `September 1, 2015 at 10:20 AM – September 5, 2015 at 12:14 PM`. <br>
   * Example of formatting when an event lasts 1 day and is in the GMT+2 time zone: `February 1, 2018, 12:10 – 2:50 PM GMT+2`.
   * @readonly
   * @maxLength 500
   */
  dateAndTime?: string | null;
  /**
   * Formatted start date of the event. Empty for TBD schedules.
   * @readonly
   * @maxLength 500
   */
  startDate?: string | null;
  /**
   * Formatted start time of the event. Empty for TBD schedules.
   * @readonly
   * @maxLength 500
   */
  startTime?: string | null;
  /**
   * Formatted end date of the event. Empty for TBD schedules or when the end date is hidden.
   * @readonly
   * @maxLength 500
   */
  endDate?: string | null;
  /**
   * Formatted end time of the event. Empty for TBD schedules or when the end date is hidden.
   * @readonly
   * @maxLength 500
   */
  endTime?: string | null;
}

/**
 * A coarse-grained representation of time duration divided into whole constituting components of days, hours, and minutes.
 * For example, 25.5 hours duration is represented as `{ days: 1, hours: 1, minutes: 30 }`.
 */
export interface TimeDuration {
  /** Number of days. */
  days?: number;
  /**
   * Number of hours.
   * @max 24
   */
  hours?: number;
  /**
   * Number of minutes.
   * @max 60
   */
  minutes?: number;
}

export enum Status {
  /** Event is published and scheduled to start. */
  UPCOMING = 'UPCOMING',
  /** Event has started. */
  STARTED = 'STARTED',
  /** Event has ended. */
  ENDED = 'ENDED',
  /** Event is canceled. */
  CANCELED = 'CANCELED',
  /** Event is not public. */
  DRAFT = 'DRAFT',
}

/** @enumType */
export type StatusWithLiterals =
  | Status
  | 'UPCOMING'
  | 'STARTED'
  | 'ENDED'
  | 'CANCELED'
  | 'DRAFT';

export interface PageUrl {
  /**
   * The base URL. For premium sites, the base is the domain. For free sites, the base is the Wix site URL (for example, `https://mysite.wixsite.com/mysite`).
   * @maxLength 500
   */
  base?: string;
  /**
   * The path to the page. For example, `/product-page/a-product`.
   * @maxLength 500
   */
  path?: string;
}

export interface Registration {
  /**
   * Reflects the current state of the registration.
   * @readonly
   */
  type?: RegistrationTypeTypeWithLiterals;
  /**
   * Registration status.
   * @readonly
   */
  status?: RegistrationStatusStatusWithLiterals;
  /** RSVP registration details. */
  rsvp?: RsvpRegistration;
  /** Ticket registration details. */
  tickets?: TicketsRegistration;
  /** External registration details. */
  external?: ExternalRegistration;
  /** Types of guests allowed to register. */
  allowedGuestTypes?: GuestTypeTypeWithLiterals;
  /**
   * Initial registration type which is set when creating an event.
   *
   * Once set, this value is immutable. To change the type of registration, use the `registration.type` field:
   * - Events with `initialType` of `RSVP` or `TICKETING` can be changed to `EXTERNAL` or `NONE`.
   * - `RSVP` events can't become `TICKETING` events, and vice versa.
   * @immutable
   */
  initialType?: InitialRegistrationTypeTypeWithLiterals;
  /** Whether the registration is paused. */
  registrationPaused?: boolean;
  /** Whether the registration is disabled. */
  registrationDisabled?: boolean;
}

export enum RegistrationTypeType {
  /** Guests register by RSVPing to the event. */
  RSVP = 'RSVP',
  /** Guests register by buying tickets. */
  TICKETING = 'TICKETING',
  /** Guests register externally using some other site or platform. */
  EXTERNAL = 'EXTERNAL',
  /** No registration is required, all guests can attend. This registration type is for only displaying the event details on a site. The property value can be changed from `RSVP` or `TICKETING `to `EXTERNAL` or `NONE` after the event is created, depending on any additional configurations applied to the event. */
  NONE = 'NONE',
}

/** @enumType */
export type RegistrationTypeTypeWithLiterals =
  | RegistrationTypeType
  | 'RSVP'
  | 'TICKETING'
  | 'EXTERNAL'
  | 'NONE';

export enum RegistrationStatusStatus {
  /** Registration status isn't applicable. */
  UNKNOWN_REGISTRATION_STATUS = 'UNKNOWN_REGISTRATION_STATUS',
  /** Registration is closed after tickets are sold out. */
  CLOSED_AUTOMATICALLY = 'CLOSED_AUTOMATICALLY',
  /** Registration is closed when the `registration.registrationDisabled` property is set to `true`. */
  CLOSED_MANUALLY = 'CLOSED_MANUALLY',
  /** Guests can start RSVPing to the event. */
  OPEN_RSVP = 'OPEN_RSVP',
  /** Guest list has reached the maximum, new guests are added to a waitlist. */
  OPEN_RSVP_WAITLIST_ONLY = 'OPEN_RSVP_WAITLIST_ONLY',
  /** Guests can buy tickets to the event. */
  OPEN_TICKETS = 'OPEN_TICKETS',
  /** Guests register on a different page or web address. */
  OPEN_EXTERNAL = 'OPEN_EXTERNAL',
  /** Registration via RSVP is scheduled for the future. */
  SCHEDULED_RSVP = 'SCHEDULED_RSVP',
}

/** @enumType */
export type RegistrationStatusStatusWithLiterals =
  | RegistrationStatusStatus
  | 'UNKNOWN_REGISTRATION_STATUS'
  | 'CLOSED_AUTOMATICALLY'
  | 'CLOSED_MANUALLY'
  | 'OPEN_RSVP'
  | 'OPEN_RSVP_WAITLIST_ONLY'
  | 'OPEN_TICKETS'
  | 'OPEN_EXTERNAL'
  | 'SCHEDULED_RSVP';

export interface RsvpRegistration {
  /** Available answers for registration to an event. */
  responseType?: ResponseTypeWithLiterals;
  /** How many guests can RSVP to an event. */
  limit?: number | null;
  /** Whether a waitlist is opened when the total guest limit is reached. If `true`, guests can RSVP to an event and are automatically added to the waitlist with the `IN_WAITLIST` status. */
  waitlistEnabled?: boolean;
  /** Registration start date. */
  startDate?: Date | null;
  /** Registration end date. */
  endDate?: Date | null;
}

export enum ResponseType {
  /** Only a **Yes** answer is available to select when RSVP'ing to an event. */
  YES_ONLY = 'YES_ONLY',
  /** *Yes** and **No** answers are available to select when RSVP'ing to an event. */
  YES_AND_NO = 'YES_AND_NO',
}

/** @enumType */
export type ResponseTypeWithLiterals = ResponseType | 'YES_ONLY' | 'YES_AND_NO';

export interface RsvpConfirmationMessages {
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveConfirmation?: PositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: PositiveResponseConfirmation;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: NegativeResponseConfirmation;
}

/** Confirmation shown after then registration when RSVP response is positive. */
export interface PositiveResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

/** Confirmation shown after then registration when RSVP response is negative. */
export interface NegativeResponseConfirmation {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export interface TicketsRegistration {
  /** Whether the registration form must be filled out separately for each ticket. */
  guestsAssignedSeparately?: boolean;
  /**
   * Ticket limit per order. <br>
   * Default: 20 tickets.
   * @max 50
   */
  ticketLimitPerOrder?: number;
  /** Ticket price currency. */
  currency?: string | null;
  /**
   * Lowest ticket price.
   * @readonly
   */
  lowestPrice?: Money;
  /**
   * Highest ticket price.
   * @readonly
   */
  highestPrice?: Money;
  /**
   * Whether all tickets are sold for the event.
   * @readonly
   */
  soldOut?: boolean | null;
  /** How tax is applied. */
  taxSettings?: TaxSettings;
  /**
   * Reservation duration in minutes.
   * @min 5
   * @max 30
   */
  reservationDurationInMinutes?: number | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Amount of money in decimal form. A period is used as a decimal separator (for example, `3.99`).
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code in 3-letter [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Amount of money in decimal form. A period or comma are used as a decimal separator (for example, `1 000,30`).
   * @readonly
   */
  formattedValue?: string | null;
}

export interface TaxSettings {
  /** Tax application settings. */
  type?: TaxTypeWithLiterals;
  /**
   * Tax name.
   * @minLength 1
   * @maxLength 10
   */
  name?: string | null;
  /**
   * Tax rate. For example, `21.55`.
   * @decimalValue options { gte:0.001, lte:100, maxScale:3 }
   */
  rate?: string | null;
  /** Apply tax to donations. */
  appliedToDonations?: boolean | null;
}

/** Tax application settings. */
export enum TaxType {
  /** Deduct the fee from the ticket price for a seller. For example, if you're selling tickets for $10, a ticket service fee of $0.25 is deducted from the price and you'll get $9.75. */
  INCLUDED_IN_PRICE = 'INCLUDED_IN_PRICE',
  /** Show the fee in addition to the ticket price at checkout and a guest pays the fee. For example, if you sell tickets for $10, a customer sees a ticket service fee of $0.25 and pays $10.25 in total. */
  ADDED_AT_CHECKOUT = 'ADDED_AT_CHECKOUT',
}

/** @enumType */
export type TaxTypeWithLiterals =
  | TaxType
  | 'INCLUDED_IN_PRICE'
  | 'ADDED_AT_CHECKOUT';

export interface TicketsConfirmationMessages {
  /**
   * Confirmation message title.
   * @maxLength 150
   */
  title?: string | null;
  /**
   * Confirmation message text.
   * @maxLength 350
   */
  message?: string | null;
  /**
   * "Download tickets" call-to-action label text.
   * @maxLength 50
   */
  downloadTicketsLabel?: string | null;
  /**
   * "Add to calendar" call-to-action label text.
   * @maxLength 50
   */
  addToCalendarActionLabel?: string | null;
  /**
   * "Share event" call-to-action label text.
   * @maxLength 50
   */
  shareActionLabel?: string | null;
}

export enum CheckoutType {
  /** Checkout using Events App. */
  EVENTS_APP = 'EVENTS_APP',
  /** Checkout using Ecomm Platform. */
  ECOMM_PLATFORM = 'ECOMM_PLATFORM',
}

/** @enumType */
export type CheckoutTypeWithLiterals =
  | CheckoutType
  | 'EVENTS_APP'
  | 'ECOMM_PLATFORM';

export interface ExternalRegistration {
  /**
   * External event registration URL.
   * @format WEB_URL
   */
  url?: string | null;
}

export enum GuestTypeType {
  /** All site visitors can RSVP to the event. */
  VISITOR_OR_MEMBER = 'VISITOR_OR_MEMBER',
  /** Only people who have signed up as members of your site are able to RSVP to the event. */
  MEMBER = 'MEMBER',
}

/** @enumType */
export type GuestTypeTypeWithLiterals =
  | GuestTypeType
  | 'VISITOR_OR_MEMBER'
  | 'MEMBER';

export enum InitialRegistrationTypeType {
  /** Guests register by RSVPing to the event. */
  RSVP = 'RSVP',
  /** Guests register by buying tickets. This property value never changes. */
  TICKETING = 'TICKETING',
}

/** @enumType */
export type InitialRegistrationTypeTypeWithLiterals =
  | InitialRegistrationTypeType
  | 'RSVP'
  | 'TICKETING';

export interface Image {
  /**
   * WixMedia image ID.
   * @minLength 1
   * @maxLength 200
   */
  id?: string;
  /** Image URL. */
  url?: string;
  /**
   * Original image height.
   *
   * **Note:** You have to define height for the image to appear.
   */
  height?: number;
  /**
   * Original image width.
   *
   * **Note:** You have to define width for the image to appear.
   */
  width?: number;
  /** Image alt text. */
  altText?: string | null;
}

export interface CalendarUrls {
  /**
   * "Add to Google Calendar" URL.
   * @readonly
   */
  google?: string;
  /**
   * "Download ICS calendar file" URL.
   * @readonly
   */
  ics?: string;
}

/**
 * The form defines which elements are displayed to a site visitor during the registration process (RSVP or checkout).
 * It also contains customizable messages and labels.
 *
 *
 * A form is an ordered list of controls (blocks), which accept guest information into a field input.
 *
 * Each control contains one or more nested inputs. For example, `Name` control has two inputs:
 * - First Name
 * - Last Name
 *
 * By default, name and email controls are always required and are pinned to the top of the form.
 */
export interface Form {
  /** Nested fields as an ordered list. */
  controls?: InputControl[];
  /**
   * Set of defined form messages displayed in the UI before, during, and after a registration flow.
   * Includes the configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
   */
  messages?: FormMessages;
}

/**
 * A block of nested fields.
 * Used to aggregate similar inputs like First Name and Last Name.
 */
export interface InputControl {
  /** Field control type. */
  type?: InputControlTypeWithLiterals;
  /** Whether the control is mandatory (such as `name` & `email`). When `true`, only the label can be changed. */
  system?: boolean;
  /**
   * Deprecated: Use `id` or `_id`.
   * @deprecated
   */
  name?: string;
  /** Child inputs. */
  inputs?: Input[];
  /**
   * *Deprecated:** Use `controls.inputs.label`.
   * @deprecated
   */
  label?: string;
  /** Field controls are sorted by this value in ascending order. */
  orderIndex?: number;
  /** Unique control ID. */
  id?: string;
  /**
   * Whether the input control is deleted.
   * @readonly
   */
  deleted?: boolean | null;
}

export enum InputControlType {
  /** Single text value field. */
  INPUT = 'INPUT',
  /** Single text value field with multiple lines. */
  TEXTAREA = 'TEXTAREA',
  /** Single-choice field with predefined values. */
  DROPDOWN = 'DROPDOWN',
  /** Single-choice field with predefined values. */
  RADIO = 'RADIO',
  /** Multiple-choice field with predefined values. */
  CHECKBOX = 'CHECKBOX',
  /** Fields for entering first and last names. */
  NAME = 'NAME',
  /** Fields for additional guests and their respective names. */
  GUEST_CONTROL = 'GUEST_CONTROL',
  /** Single-line address field. */
  ADDRESS_SHORT = 'ADDRESS_SHORT',
  /** Full address field with multiple lines. */
  ADDRESS_FULL = 'ADDRESS_FULL',
  /** Fields for entering year, month, and day. */
  DATE = 'DATE',
}

/** @enumType */
export type InputControlTypeWithLiterals =
  | InputControlType
  | 'INPUT'
  | 'TEXTAREA'
  | 'DROPDOWN'
  | 'RADIO'
  | 'CHECKBOX'
  | 'NAME'
  | 'GUEST_CONTROL'
  | 'ADDRESS_SHORT'
  | 'ADDRESS_FULL'
  | 'DATE';

/** Child inputs. */
export interface Input {
  /** Field name. */
  name?: string;
  /**
   * *Deprecated:** Use `controls.inputs.type.TEXT_ARRAY`.
   * @deprecated
   */
  array?: boolean;
  /** Main field label. */
  label?: string;
  /** Additional labels for multi-valued fields such as address. */
  additionalLabels?: Record<string, string>;
  /** Predefined choice options for fields, such as dropdown. */
  options?: string[];
  /** Whether field is mandatory. */
  mandatory?: boolean;
  /** Maximum number of accepted characters (relevant for text fields). */
  maxLength?: number;
  /**
   * Type which determines field format.
   * Used to validate submitted response.
   */
  type?: ValueTypeWithLiterals;
  /**
   * The maximum number of accepted values for array input.
   *
   * **Note:** Only applicable for `TEXT_ARRAY` input fields.
   */
  maxSize?: number | null;
  /**
   * Default option initially selected when an input has multiple choices.
   *
   * Defaults to first (0th) option, if not configured.
   * Currently only applicable for `type.dropdown`.
   */
  defaultOptionSelection?: OptionSelection;
  /**
   * Additional labels for multi-valued fields, such as address.
   * @readonly
   */
  labels?: Label[];
}

export enum ValueType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  TEXT_ARRAY = 'TEXT_ARRAY',
  DATE_TIME = 'DATE_TIME',
  ADDRESS = 'ADDRESS',
}

/** @enumType */
export type ValueTypeWithLiterals =
  | ValueType
  | 'TEXT'
  | 'NUMBER'
  | 'TEXT_ARRAY'
  | 'DATE_TIME'
  | 'ADDRESS';

/**
 * Describes initially selected option when an input has multiple choices.
 * Defaults to first (0th) option if not configured.
 */
export interface OptionSelection extends OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

/** @oneof */
export interface OptionSelectionSelectedOptionOneOf {
  /**
   * 0-based index from predefined `controls.inputs.options` which is initial selection.
   * @max 199
   */
  optionIndex?: number;
  /**
   * Placeholder hint describing expected choices, such as "Please select".
   * Considered an empty choice.
   * @maxLength 200
   */
  placeholderText?: string;
}

export interface Label {
  /** Field name. */
  name?: string;
  /** Field label. */
  label?: string;
}

/**
 * Defines form messages shown in UI before, during, and after registration flow.
 * It enables configuration of form titles, response labels, "thank you" messages, and call-to-action texts.
 */
export interface FormMessages {
  /** [RSVP form](https://dev.wix.com/docs/rest/business-solutions/events/rsvp-v2/introduction) messages. */
  rsvp?: RsvpFormMessages;
  /** Checkout form messages. */
  checkout?: CheckoutFormMessages;
  /** Messages shown when event registration is closed. */
  registrationClosed?: RegistrationClosedMessages;
  /** Messages shown when event tickets are unavailable. */
  ticketsUnavailable?: TicketsUnavailableMessages;
}

export interface RsvpFormMessages {
  /** Label text indicating RSVP's `status` is `"YES"`. */
  rsvpYesOption?: string;
  /** Label text indicating RSVP's `status` is `"NO"`. */
  rsvpNoOption?: string;
  /** Messages displayed when an RSVP's `status` is set to `"YES"`. */
  positiveMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"WAITLIST"`, for when the event is full and a waitlist is available). */
  waitlistMessages?: Positive;
  /** Messages displayed when an RSVP's `status` is set to `"NO"`. */
  negativeMessages?: Negative;
  /** "Submit form" call-to-action label text. */
  submitActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface RsvpFormMessagesPositiveResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarActionLabel?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Confirmation messages shown after registration. */
export interface RsvpFormMessagesNegativeResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** "Share event" call-to-action label text. */
  shareActionLabel?: string;
}

/** Set of messages shown during registration when RSVP response is positive. */
export interface Positive {
  /** Main form title for positive response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: RsvpFormMessagesPositiveResponseConfirmation;
}

/** A set of messages shown during registration with negative response */
export interface Negative {
  /** Main form title for negative response. */
  title?: string;
  /** Confirmation messages shown after registration. */
  confirmation?: RsvpFormMessagesNegativeResponseConfirmation;
}

export interface CheckoutFormMessages {
  /** Main form title for response. */
  title?: string;
  /** Submit form call-to-action label text. */
  submitActionLabel?: string;
  /** Confirmation messages shown after checkout. */
  confirmation?: ResponseConfirmation;
}

/** Confirmation messages shown after checkout. */
export interface ResponseConfirmation {
  /** Confirmation message title. */
  title?: string;
  /** Confirmation message text. */
  message?: string;
  /** "Download tickets" call-to-action label text. */
  downloadTicketsLabel?: string;
  /** "Add to calendar" call-to-action label text. */
  addToCalendarLabel?: string;
  /** "Share event" call-to-action label text. */
  shareEventLabel?: string;
}

export interface RegistrationClosedMessages {
  /** Message shown when event registration is closed. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface TicketsUnavailableMessages {
  /** Message shown when event tickets are unavailable. */
  message?: string;
  /** "Explore other events" call-to-action label text. */
  exploreEventsActionLabel?: string;
}

export interface Summaries {
  /** RSVP summary of guests. */
  rsvps?: Rsvps;
  /** Summary of revenue and sold tickets. Archived orders aren't included. */
  tickets?: Tickets;
}

export interface EventsMoney {
  /**
   * *Deprecated:** Use `value` instead.
   * @format DECIMAL_VALUE
   * @deprecated
   */
  amount?: string;
  /**
   * 3-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format. For example, `USD`.
   * @format CURRENCY
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, starts with a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string | null;
}

export interface Rsvps {
  /**
   * Total number of RSVPs.
   * @readonly
   */
  totalCount?: number;
  /**
   * Number of RSVPs with status `YES`.
   * @readonly
   */
  yesCount?: number;
  /**
   * Number of RSVPs with status `NO`.
   * @readonly
   */
  noCount?: number;
  /**
   * Number of RSVPs in a waitlist.
   * @readonly
   */
  waitlistCount?: number;
}

export interface Tickets {
  /**
   * Number of sold tickets.
   * @readonly
   */
  ticketsSold?: number;
  /**
   * Total revenue. Taxes and payment provider fees aren't deducted.
   * @readonly
   */
  revenue?: EventsMoney;
  /**
   * Whether the currency is locked and can't be changed.
   * @readonly
   */
  currencyLocked?: boolean;
  /**
   * Number of orders placed.
   * @readonly
   */
  totalOrders?: number;
  /**
   * Total balance of confirmed transactions.
   * @readonly
   */
  totalSales?: EventsMoney;
}

export interface GuestListSettings {
  /** Whether the guest list is displayed publicly to all guests. */
  displayedPublicly?: boolean;
}

export interface Feed {
  /** Event discussion feed token. */
  token?: string;
}

export interface OnlineConferencing {
  /** Whether online conferencing is enabled. Not applicable for events where date and time are TBD. When enabled, links to join the conference are generated and provided to guests. */
  enabled?: boolean;
  /**
   * Conference host ID.
   * @format GUID
   */
  providerId?: string | null;
  /** Conference type. */
  type?: ConferenceTypeTypeWithLiterals;
  /** Online conferencing session information. */
  session?: OnlineConferencingSession;
}

export enum ConferenceTypeType {
  /** Guests can do some actions during the conference, for example talk, turn on their camera and show their screen. */
  MEETING = 'MEETING',
  /** Guests can only watch the conference. */
  WEBINAR = 'WEBINAR',
}

/** @enumType */
export type ConferenceTypeTypeWithLiterals =
  | ConferenceTypeType
  | 'MEETING'
  | 'WEBINAR';

export interface OnlineConferencingSession {
  /**
   * Link for the event host to start the online conference session.
   * @readonly
   */
  hostLink?: string;
  /**
   * Link for guests to join the online conference session.
   * @readonly
   */
  guestLink?: string;
  /**
   * Password required to join the online conferencing session (when relevant).
   * @readonly
   */
  password?: string | null;
  /**
   * Whether the session was created successfully on the event host side.
   * @readonly
   */
  sessionCreated?: boolean | null;
  /**
   * Unique session ID.
   * @readonly
   */
  sessionId?: string | null;
}

export interface SeoSettings {
  /**
   * URL slug.
   * @maxLength 130
   */
  slug?: string;
  /** Advanced SEO data. */
  advancedSeoData?: SeoSchema;
  /**
   * Whether the slug is hidden from the SEO sitemap.
   * @readonly
   */
  hidden?: boolean | null;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface AgendaSettings {
  /** Whether the schedule is enabled for the event. */
  enabled?: boolean;
  /**
   * Schedule page URL.
   * @readonly
   */
  pageUrl?: PageUrl;
}

export interface EventCategories {
  /**
   * Event categories.
   * @maxSize 10
   */
  categories?: EventCategory[];
}

export interface EventCategory {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  id?: string;
  /**
   * Category name.
   * @minLength 1
   * @maxLength 30
   */
  name?: string;
  /**
   * Category creation timestamp.
   * @readonly
   */
  createdDate?: Date | null;
  /**
   * Category is hidden.
   * @readonly
   */
  hidden?: boolean;
  /**
   * Category type.
   * @readonly
   */
  type?: TypeWithLiterals;
}

export enum Type {
  /** Created manually by the user. */
  MANUAL = 'MANUAL',
  /** Created automatically when publishing recurring events. */
  RECURRING_EVENT = 'RECURRING_EVENT',
}

/** @enumType */
export type TypeWithLiterals = Type | 'MANUAL' | 'RECURRING_EVENT';

export interface EventDisplaySettings {
  /** Whether event details button is hidden. Only available for events with no registration. */
  hideEventDetailsButton?: boolean | null;
  /** Disables event details page visibility. If event has an external registration configured visitors will be redirected from this page. */
  hideEventDetailsPage?: boolean | null;
}

export interface LabellingSettings {}

export interface RichContent {
  /** Node objects representing a rich content document. */
  nodes?: Node[];
  /** Object metadata. */
  metadata?: Metadata;
  /** Global styling for header, paragraph, block quote, and code block nodes in the object. */
  documentStyle?: DocumentStyle;
}

export interface Node extends NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
  /** Node type. Use `APP_EMBED` for nodes that embed content from other Wix apps. Use `EMBED` to embed content in [oEmbed](https://oembed.com/) format. */
  type?: NodeTypeWithLiterals;
  /** Node ID. */
  id?: string;
  /** A list of child nodes. */
  nodes?: Node[];
  /** Padding and background color styling for the node. */
  style?: NodeStyle;
}

/** @oneof */
export interface NodeDataOneOf {
  /** Data for a button node. */
  buttonData?: ButtonData;
  /** Data for a code block node. */
  codeBlockData?: CodeBlockData;
  /** Data for a divider node. */
  dividerData?: DividerData;
  /** Data for a file node. */
  fileData?: FileData;
  /** Data for a gallery node. */
  galleryData?: GalleryData;
  /** Data for a GIF node. */
  gifData?: GIFData;
  /** Data for a heading node. */
  headingData?: HeadingData;
  /** Data for an embedded HTML node. */
  htmlData?: HTMLData;
  /** Data for an image node. */
  imageData?: ImageData;
  /** Data for a link preview node. */
  linkPreviewData?: LinkPreviewData;
  /** @deprecated */
  mapData?: MapData;
  /** Data for a paragraph node. */
  paragraphData?: ParagraphData;
  /** Data for a poll node. */
  pollData?: PollData;
  /** Data for a text node. Used to apply decorations to text. */
  textData?: TextData;
  /** Data for an app embed node. */
  appEmbedData?: AppEmbedData;
  /** Data for a video node. */
  videoData?: VideoData;
  /** Data for an oEmbed node. */
  embedData?: EmbedData;
  /** Data for a collapsible list node. */
  collapsibleListData?: CollapsibleListData;
  /** Data for a table node. */
  tableData?: TableData;
  /** Data for a table cell node. */
  tableCellData?: TableCellData;
  /** Data for a custom external node. */
  externalData?: Record<string, any> | null;
  /** Data for an audio node. */
  audioData?: AudioData;
  /** Data for an ordered list node. */
  orderedListData?: OrderedListData;
  /** Data for a bulleted list node. */
  bulletedListData?: BulletedListData;
  /** Data for a block quote node. */
  blockquoteData?: BlockquoteData;
  /** Data for a caption node. */
  captionData?: CaptionData;
  /** Data for a layout node. Reserved for future use. */
  layoutData?: LayoutData;
  /** Data for a cell node. */
  layoutCellData?: LayoutCellData;
  /** Data for a shape node. */
  shapeData?: ShapeData;
  /** Data for a card node. */
  cardData?: CardData;
  /** Data for a table of contents node. */
  tocData?: TocData;
  /** Data for a smart block node. */
  smartBlockData?: SmartBlockData;
  /** Data for a smart block cell node. */
  smartBlockCellData?: SmartBlockCellData;
}

export enum NodeType {
  PARAGRAPH = 'PARAGRAPH',
  TEXT = 'TEXT',
  HEADING = 'HEADING',
  BULLETED_LIST = 'BULLETED_LIST',
  ORDERED_LIST = 'ORDERED_LIST',
  LIST_ITEM = 'LIST_ITEM',
  BLOCKQUOTE = 'BLOCKQUOTE',
  CODE_BLOCK = 'CODE_BLOCK',
  VIDEO = 'VIDEO',
  DIVIDER = 'DIVIDER',
  FILE = 'FILE',
  GALLERY = 'GALLERY',
  GIF = 'GIF',
  HTML = 'HTML',
  IMAGE = 'IMAGE',
  LINK_PREVIEW = 'LINK_PREVIEW',
  /** @deprecated */
  MAP = 'MAP',
  POLL = 'POLL',
  APP_EMBED = 'APP_EMBED',
  BUTTON = 'BUTTON',
  COLLAPSIBLE_LIST = 'COLLAPSIBLE_LIST',
  TABLE = 'TABLE',
  EMBED = 'EMBED',
  COLLAPSIBLE_ITEM = 'COLLAPSIBLE_ITEM',
  COLLAPSIBLE_ITEM_TITLE = 'COLLAPSIBLE_ITEM_TITLE',
  COLLAPSIBLE_ITEM_BODY = 'COLLAPSIBLE_ITEM_BODY',
  TABLE_CELL = 'TABLE_CELL',
  TABLE_ROW = 'TABLE_ROW',
  EXTERNAL = 'EXTERNAL',
  AUDIO = 'AUDIO',
  CAPTION = 'CAPTION',
  LAYOUT = 'LAYOUT',
  LAYOUT_CELL = 'LAYOUT_CELL',
  SHAPE = 'SHAPE',
  CARD = 'CARD',
  TOC = 'TOC',
  SMART_BLOCK = 'SMART_BLOCK',
  SMART_BLOCK_CELL = 'SMART_BLOCK_CELL',
}

/** @enumType */
export type NodeTypeWithLiterals =
  | NodeType
  | 'PARAGRAPH'
  | 'TEXT'
  | 'HEADING'
  | 'BULLETED_LIST'
  | 'ORDERED_LIST'
  | 'LIST_ITEM'
  | 'BLOCKQUOTE'
  | 'CODE_BLOCK'
  | 'VIDEO'
  | 'DIVIDER'
  | 'FILE'
  | 'GALLERY'
  | 'GIF'
  | 'HTML'
  | 'IMAGE'
  | 'LINK_PREVIEW'
  | 'MAP'
  | 'POLL'
  | 'APP_EMBED'
  | 'BUTTON'
  | 'COLLAPSIBLE_LIST'
  | 'TABLE'
  | 'EMBED'
  | 'COLLAPSIBLE_ITEM'
  | 'COLLAPSIBLE_ITEM_TITLE'
  | 'COLLAPSIBLE_ITEM_BODY'
  | 'TABLE_CELL'
  | 'TABLE_ROW'
  | 'EXTERNAL'
  | 'AUDIO'
  | 'CAPTION'
  | 'LAYOUT'
  | 'LAYOUT_CELL'
  | 'SHAPE'
  | 'CARD'
  | 'TOC'
  | 'SMART_BLOCK'
  | 'SMART_BLOCK_CELL';

export interface NodeStyle {
  /** The top padding value in pixels. */
  paddingTop?: string | null;
  /** The bottom padding value in pixels. */
  paddingBottom?: string | null;
  /** The background color as a hexadecimal value. */
  backgroundColor?: string | null;
}

export interface ButtonData {
  /** Styling for the button's container. */
  containerData?: PluginContainerData;
  /** The button type. */
  type?: ButtonDataTypeWithLiterals;
  /** Styling for the button. */
  styles?: Styles;
  /** The text to display on the button. */
  text?: string | null;
  /** Button link details. */
  link?: Link;
}

/** Background type */
export enum BackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackgroundTypeWithLiterals = BackgroundType | 'COLOR' | 'GRADIENT';

export interface Gradient {
  /** Gradient type. */
  type?: GradientTypeWithLiterals;
  /**
   * Color stops for the gradient.
   * @maxSize 1000
   */
  stops?: Stop[];
  /** Angle in degrees for linear gradient (0-360). */
  angle?: number | null;
  /**
   * Horizontal center position for radial gradient (0-100).
   * @max 100
   */
  centerX?: number | null;
  /**
   * Vertical center position for radial gradient (0-100).
   * @max 100
   */
  centerY?: number | null;
}

/** Gradient type. */
export enum GradientType {
  /** Linear gradient. */
  LINEAR = 'LINEAR',
  /** Radial gradient. */
  RADIAL = 'RADIAL',
}

/** @enumType */
export type GradientTypeWithLiterals = GradientType | 'LINEAR' | 'RADIAL';

/** A single color stop in the gradient. */
export interface Stop {
  /**
   * Stop color as hex value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Stop position (0-1). */
  position?: number | null;
}

export interface Border {
  /**
   * Deprecated: Use `borderWidth` in `styles` instead.
   * @deprecated
   */
  width?: number | null;
  /**
   * Deprecated: Use `borderRadius` in `styles` instead.
   * @deprecated
   */
  radius?: number | null;
}

export interface Colors {
  /**
   * Deprecated: Use `textColor` in `styles` instead.
   * @deprecated
   */
  text?: string | null;
  /**
   * Deprecated: Use `borderColor` in `styles` instead.
   * @deprecated
   */
  border?: string | null;
  /**
   * Deprecated: Use `backgroundColor` in `styles` instead.
   * @deprecated
   */
  background?: string | null;
}

/** Background styling (color or gradient) */
export interface Background {
  /** Background type. */
  type?: BackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface PluginContainerData {
  /** The width of the node when it's displayed. */
  width?: PluginContainerDataWidth;
  /** The node's alignment within its container. */
  alignment?: PluginContainerDataAlignmentWithLiterals;
  /** Spoiler cover settings for the node. */
  spoiler?: Spoiler;
  /** The height of the node when it's displayed. */
  height?: Height;
  /** Sets whether text should wrap around this node when it's displayed. If `textWrap` is `false`, the node takes up the width of its container. Defaults to `true` for all node types except 'DIVIVDER' where it defaults to `false`. */
  textWrap?: boolean | null;
}

export enum WidthType {
  /** Width matches the content width */
  CONTENT = 'CONTENT',
  /** Small Width */
  SMALL = 'SMALL',
  /** Width will match the original asset width */
  ORIGINAL = 'ORIGINAL',
  /** coast-to-coast display */
  FULL_WIDTH = 'FULL_WIDTH',
}

/** @enumType */
export type WidthTypeWithLiterals =
  | WidthType
  | 'CONTENT'
  | 'SMALL'
  | 'ORIGINAL'
  | 'FULL_WIDTH';

export interface PluginContainerDataWidth
  extends PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

/** @oneof */
export interface PluginContainerDataWidthDataOneOf {
  /**
   * One of the following predefined width options:
   * `CONTENT`: The width of the container matches the content width.
   * `SMALL`: A small width.
   * `ORIGINAL`: For `imageData` containers only. The width of the container matches the original image width.
   * `FULL_WIDTH`: For `imageData` containers only. The image container takes up the full width of the screen.
   */
  size?: WidthTypeWithLiterals;
  /** A custom width value in pixels. */
  custom?: string | null;
}

export enum PluginContainerDataAlignment {
  /** Center Alignment */
  CENTER = 'CENTER',
  /** Left Alignment */
  LEFT = 'LEFT',
  /** Right Alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type PluginContainerDataAlignmentWithLiterals =
  | PluginContainerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface Spoiler {
  /** Sets whether the spoiler cover is enabled for this node. Defaults to `false`. */
  enabled?: boolean | null;
  /** The description displayed on top of the spoiler cover. */
  description?: string | null;
  /** The text for the button used to remove the spoiler cover. */
  buttonText?: string | null;
}

export interface Height {
  /** A custom height value in pixels. */
  custom?: string | null;
}

export enum ButtonDataType {
  /** Regular link button */
  LINK = 'LINK',
  /** Triggers custom action that is defined in plugin configuration by the consumer */
  ACTION = 'ACTION',
}

/** @enumType */
export type ButtonDataTypeWithLiterals = ButtonDataType | 'LINK' | 'ACTION';

export interface Styles {
  /**
   * Deprecated: Use `borderWidth` and `borderRadius` instead.
   * @deprecated
   */
  border?: Border;
  /**
   * Deprecated: Use `textColor`, `borderColor` and `backgroundColor` instead.
   * @deprecated
   */
  colors?: Colors;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /**
   * Deprecated: Use `borderWidth` for normal/hover states instead.
   * @deprecated
   */
  borderWidthHover?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /**
   * Deprecated: Use `backgroundHover` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
  /** Background styling (color or gradient). */
  background?: Background;
  /** Background styling for hover state (color or gradient). */
  backgroundHover?: Background;
}

export interface Link extends LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
  /**
   * he HTML `target` attribute value for the link. This property defines where the linked document opens as follows:
   * `SELF` - Default. Opens the linked document in the same frame as the link.
   * `BLANK` - Opens the linked document in a new browser tab or window.
   * `PARENT` - Opens the linked document in the link's parent frame.
   * `TOP` - Opens the linked document in the full body of the link's browser tab or window.
   */
  target?: TargetWithLiterals;
  /** The HTML `rel` attribute value for the link. This object specifies the relationship between the current document and the linked document. */
  rel?: Rel;
  /** A serialized object used for a custom or external link panel. */
  customData?: string | null;
}

/** @oneof */
export interface LinkDataOneOf {
  /** The absolute URL for the linked document. */
  url?: string;
  /** The target node's ID. Used for linking to another node in this object. */
  anchor?: string;
}

export enum Target {
  /** Opens the linked document in the same frame as it was clicked (this is default) */
  SELF = 'SELF',
  /** Opens the linked document in a new window or tab */
  BLANK = 'BLANK',
  /** Opens the linked document in the parent frame */
  PARENT = 'PARENT',
  /** Opens the linked document in the full body of the window */
  TOP = 'TOP',
}

/** @enumType */
export type TargetWithLiterals = Target | 'SELF' | 'BLANK' | 'PARENT' | 'TOP';

export interface Rel {
  /** Indicates to search engine crawlers not to follow the link. Defaults to `false`. */
  nofollow?: boolean | null;
  /** Indicates to search engine crawlers that the link is a paid placement such as sponsored content or an advertisement. Defaults to `false`. */
  sponsored?: boolean | null;
  /** Indicates that this link is user-generated content and isn't necessarily trusted or endorsed by the page’s author. For example, a link in a fourm post. Defaults to `false`. */
  ugc?: boolean | null;
  /** Indicates that this link protect referral information from being passed to the target website. */
  noreferrer?: boolean | null;
}

export interface CodeBlockData {
  /** Styling for the code block's text. */
  textStyle?: TextStyle;
}

export interface TextStyle {
  /** Text alignment. Defaults to `AUTO`. */
  textAlignment?: TextAlignmentWithLiterals;
  /** A CSS `line-height` value for the text expressed as a ratio relative to the font size. For example, if the font size is 20px, a `lineHeight` value of `'1.5'`` results in a line height of 30px. */
  lineHeight?: string | null;
}

export enum TextAlignment {
  /** browser default, eqivalent to `initial` */
  AUTO = 'AUTO',
  /** Left align */
  LEFT = 'LEFT',
  /** Right align */
  RIGHT = 'RIGHT',
  /** Center align */
  CENTER = 'CENTER',
  /** Text is spaced to line up its left and right edges to the left and right edges of the line box, except for the last line */
  JUSTIFY = 'JUSTIFY',
}

/** @enumType */
export type TextAlignmentWithLiterals =
  | TextAlignment
  | 'AUTO'
  | 'LEFT'
  | 'RIGHT'
  | 'CENTER'
  | 'JUSTIFY';

export interface DividerData {
  /** Styling for the divider's container. */
  containerData?: PluginContainerData;
  /** Divider line style. */
  lineStyle?: LineStyleWithLiterals;
  /** Divider width. */
  width?: WidthWithLiterals;
  /** Divider alignment. */
  alignment?: DividerDataAlignmentWithLiterals;
}

export enum LineStyle {
  /** Single Line */
  SINGLE = 'SINGLE',
  /** Double Line */
  DOUBLE = 'DOUBLE',
  /** Dashed Line */
  DASHED = 'DASHED',
  /** Dotted Line */
  DOTTED = 'DOTTED',
}

/** @enumType */
export type LineStyleWithLiterals =
  | LineStyle
  | 'SINGLE'
  | 'DOUBLE'
  | 'DASHED'
  | 'DOTTED';

export enum Width {
  /** Large line */
  LARGE = 'LARGE',
  /** Medium line */
  MEDIUM = 'MEDIUM',
  /** Small line */
  SMALL = 'SMALL',
}

/** @enumType */
export type WidthWithLiterals = Width | 'LARGE' | 'MEDIUM' | 'SMALL';

export enum DividerDataAlignment {
  /** Center alignment */
  CENTER = 'CENTER',
  /** Left alignment */
  LEFT = 'LEFT',
  /** Right alignment */
  RIGHT = 'RIGHT',
}

/** @enumType */
export type DividerDataAlignmentWithLiterals =
  | DividerDataAlignment
  | 'CENTER'
  | 'LEFT'
  | 'RIGHT';

export interface FileData {
  /** Styling for the file's container. */
  containerData?: PluginContainerData;
  /** The source for the file's data. */
  src?: FileSource;
  /** File name. */
  name?: string | null;
  /** File type. */
  type?: string | null;
  /**
   * Use `sizeInKb` instead.
   * @deprecated
   */
  size?: number | null;
  /** Settings for PDF files. */
  pdfSettings?: PDFSettings;
  /** File MIME type. */
  mimeType?: string | null;
  /** File path. */
  path?: string | null;
  /** File size in KB. */
  sizeInKb?: string | null;
}

export enum ViewMode {
  /** No PDF view */
  NONE = 'NONE',
  /** Full PDF view */
  FULL = 'FULL',
  /** Mini PDF view */
  MINI = 'MINI',
}

/** @enumType */
export type ViewModeWithLiterals = ViewMode | 'NONE' | 'FULL' | 'MINI';

export interface FileSource extends FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
  /** Indicates whether the file's source is private. Defaults to `false`. */
  private?: boolean | null;
}

/** @oneof */
export interface FileSourceDataOneOf {
  /** The absolute URL for the file's source. */
  url?: string | null;
  /**
   * Custom ID. Use `id` instead.
   * @deprecated
   */
  custom?: string | null;
  /** An ID that's resolved to a URL by a resolver function. */
  id?: string | null;
}

export interface PDFSettings {
  /**
   * PDF view mode. One of the following:
   * `NONE` : The PDF isn't displayed.
   * `FULL` : A full page view of the PDF is displayed.
   * `MINI` : A mini view of the PDF is displayed.
   */
  viewMode?: ViewModeWithLiterals;
  /** Sets whether the PDF download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the PDF print button is disabled. Defaults to `false`. */
  disablePrint?: boolean | null;
}

export interface GalleryData {
  /** Styling for the gallery's container. */
  containerData?: PluginContainerData;
  /** The items in the gallery. */
  items?: Item[];
  /** Options for defining the gallery's appearance. */
  options?: GalleryOptions;
  /** Sets whether the gallery's expand button is disabled. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Sets whether the gallery's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
}

export interface Media {
  /** The source for the media's data. */
  src?: FileSource;
  /** Media width in pixels. */
  width?: number | null;
  /** Media height in pixels. */
  height?: number | null;
  /** Media duration in seconds. Only relevant for audio and video files. */
  duration?: number | null;
}

export interface ItemImage {
  /** Image file details. */
  media?: Media;
  /** Link details for images that are links. */
  link?: Link;
}

export interface Video {
  /** Video file details. */
  media?: Media;
  /** Video thumbnail file details. */
  thumbnail?: Media;
}

export interface Item extends ItemDataOneOf {
  /** An image item. */
  image?: ItemImage;
  /** A video item. */
  video?: Video;
  /** Item title. */
  title?: string | null;
  /** Item's alternative text. */
  altText?: string | null;
}

/** @oneof */
export interface ItemDataOneOf {
  /** An image item. */
  image?: ItemImage;
  /** A video item. */
  video?: Video;
}

export interface GalleryOptions {
  /** Gallery layout. */
  layout?: GalleryOptionsLayout;
  /** Styling for gallery items. */
  item?: ItemStyle;
  /** Styling for gallery thumbnail images. */
  thumbnails?: Thumbnails;
}

export enum LayoutType {
  /** Collage type */
  COLLAGE = 'COLLAGE',
  /** Masonry type */
  MASONRY = 'MASONRY',
  /** Grid type */
  GRID = 'GRID',
  /** Thumbnail type */
  THUMBNAIL = 'THUMBNAIL',
  /** Slider type */
  SLIDER = 'SLIDER',
  /** Slideshow type */
  SLIDESHOW = 'SLIDESHOW',
  /** Panorama type */
  PANORAMA = 'PANORAMA',
  /** Column type */
  COLUMN = 'COLUMN',
  /** Magic type */
  MAGIC = 'MAGIC',
  /** Fullsize images type */
  FULLSIZE = 'FULLSIZE',
}

/** @enumType */
export type LayoutTypeWithLiterals =
  | LayoutType
  | 'COLLAGE'
  | 'MASONRY'
  | 'GRID'
  | 'THUMBNAIL'
  | 'SLIDER'
  | 'SLIDESHOW'
  | 'PANORAMA'
  | 'COLUMN'
  | 'MAGIC'
  | 'FULLSIZE';

export enum Orientation {
  /** Rows Orientation */
  ROWS = 'ROWS',
  /** Columns Orientation */
  COLUMNS = 'COLUMNS',
}

/** @enumType */
export type OrientationWithLiterals = Orientation | 'ROWS' | 'COLUMNS';

export enum Crop {
  /** Crop to fill */
  FILL = 'FILL',
  /** Crop to fit */
  FIT = 'FIT',
}

/** @enumType */
export type CropWithLiterals = Crop | 'FILL' | 'FIT';

export enum ThumbnailsAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Right alignment */
  RIGHT = 'RIGHT',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
  /** Left alignment */
  LEFT = 'LEFT',
  /** No thumbnail */
  NONE = 'NONE',
}

/** @enumType */
export type ThumbnailsAlignmentWithLiterals =
  | ThumbnailsAlignment
  | 'TOP'
  | 'RIGHT'
  | 'BOTTOM'
  | 'LEFT'
  | 'NONE';

export interface GalleryOptionsLayout {
  /** Gallery layout type. */
  type?: LayoutTypeWithLiterals;
  /** Sets whether horizontal scroll is enabled. Defaults to `true` unless the layout `type` is set to `GRID` or `COLLAGE`. */
  horizontalScroll?: boolean | null;
  /** Gallery orientation. */
  orientation?: OrientationWithLiterals;
  /** The number of columns to display on full size screens. */
  numberOfColumns?: number | null;
  /** The number of columns to display on mobile screens. */
  mobileNumberOfColumns?: number | null;
}

export interface ItemStyle {
  /** Desirable dimension for each item in pixels (behvaior changes according to gallery type) */
  targetSize?: number | null;
  /** Item ratio */
  ratio?: number | null;
  /** Sets how item images are cropped. */
  crop?: CropWithLiterals;
  /** The spacing between items in pixels. */
  spacing?: number | null;
}

export interface Thumbnails {
  /** Thumbnail alignment. */
  placement?: ThumbnailsAlignmentWithLiterals;
  /** Spacing between thumbnails in pixels. */
  spacing?: number | null;
}

export interface GIFData {
  /** Styling for the GIF's container. */
  containerData?: PluginContainerData;
  /** The source of the full size GIF. */
  original?: GIF;
  /** The source of the downsized GIF. */
  downsized?: GIF;
  /** Height in pixels. */
  height?: number;
  /** Width in pixels. */
  width?: number;
  /** Type of GIF (Sticker or NORMAL). Defaults to `NORMAL`. */
  gifType?: GIFTypeWithLiterals;
}

export interface GIF {
  /**
   * GIF format URL.
   * @format WEB_URL
   */
  gif?: string | null;
  /**
   * MP4 format URL.
   * @format WEB_URL
   */
  mp4?: string | null;
  /**
   * Thumbnail URL.
   * @format WEB_URL
   */
  still?: string | null;
}

export enum GIFType {
  NORMAL = 'NORMAL',
  STICKER = 'STICKER',
}

/** @enumType */
export type GIFTypeWithLiterals = GIFType | 'NORMAL' | 'STICKER';

export interface HeadingData {
  /** Heading level from 1-6. */
  level?: number;
  /** Styling for the heading text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Rendered heading level for SEO/accessibility, overrides the HTML tag when set. */
  renderedLevel?: number | null;
}

export interface HTMLData extends HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
  /** Styling for the HTML node's container. Height property is irrelevant for HTML embeds when autoHeight is set to `true`. */
  containerData?: PluginContainerData;
  /** The type of HTML code. */
  source?: SourceWithLiterals;
  /** If container height is aligned with its content height. Defaults to `true`. */
  autoHeight?: boolean | null;
}

/** @oneof */
export interface HTMLDataDataOneOf {
  /** The URL for the HTML code for the node. */
  url?: string;
  /** The HTML code for the node. */
  html?: string;
  /**
   * Whether this is an AdSense element. Use `source` instead.
   * @deprecated
   */
  isAdsense?: boolean | null;
  /** The WixelWidget ID for AI_WIDGET source nodes. */
  widgetId?: string;
}

export enum Source {
  HTML = 'HTML',
  ADSENSE = 'ADSENSE',
  AI = 'AI',
  AI_WIDGET = 'AI_WIDGET',
}

/** @enumType */
export type SourceWithLiterals =
  | Source
  | 'HTML'
  | 'ADSENSE'
  | 'AI'
  | 'AI_WIDGET';

export interface ImageData {
  /** Styling for the image's container. */
  containerData?: PluginContainerData;
  /** Image file details. */
  image?: Media;
  /** Link details for images that are links. */
  link?: Link;
  /** Sets whether the image expands to full screen when clicked. Defaults to `false`. */
  disableExpand?: boolean | null;
  /** Image's alternative text. */
  altText?: string | null;
  /**
   * Deprecated: use Caption node instead.
   * @deprecated
   */
  caption?: string | null;
  /** Sets whether the image's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Sets whether the image is decorative and does not need an explanation. Defaults to `false`. */
  decorative?: boolean | null;
  /** Styling for the image. */
  styles?: ImageDataStyles;
}

export interface StylesBorder {
  /** Border width in pixels. */
  width?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Border radius in pixels. */
  radius?: number | null;
}

export interface ImageDataStyles {
  /** Border attributes. */
  border?: StylesBorder;
}

export interface LinkPreviewData {
  /** Styling for the link preview's container. */
  containerData?: PluginContainerData;
  /** Link details. */
  link?: Link;
  /** Preview title. */
  title?: string | null;
  /** Preview thumbnail URL. */
  thumbnailUrl?: string | null;
  /** Preview description. */
  description?: string | null;
  /** The preview content as HTML. */
  html?: string | null;
  /** Styling for the link preview. */
  styles?: LinkPreviewDataStyles;
}

export enum StylesPosition {
  /** Thumbnail positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Thumbnail positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Thumbnail positioned at the top */
  TOP = 'TOP',
  /** Thumbnail hidden and not displayed */
  HIDDEN = 'HIDDEN',
}

/** @enumType */
export type StylesPositionWithLiterals =
  | StylesPosition
  | 'START'
  | 'END'
  | 'TOP'
  | 'HIDDEN';

export interface LinkPreviewDataStyles {
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Title color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Subtitle color as a hexadecimal value.
   * @format COLOR_HEX
   */
  subtitleColor?: string | null;
  /**
   * Link color as a hexadecimal value.
   * @format COLOR_HEX
   */
  linkColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Position of thumbnail. Defaults to `START`. */
  thumbnailPosition?: StylesPositionWithLiterals;
}

export interface MapData {
  /** Styling for the map's container. */
  containerData?: PluginContainerData;
  /** Map settings. */
  mapSettings?: MapSettings;
}

export interface MapSettings {
  /** The address to display on the map. */
  address?: string | null;
  /** Sets whether the map is draggable. */
  draggable?: boolean | null;
  /** Sets whether the location marker is visible. */
  marker?: boolean | null;
  /** Sets whether street view control is enabled. */
  streetViewControl?: boolean | null;
  /** Sets whether zoom control is enabled. */
  zoomControl?: boolean | null;
  /** Location latitude. */
  lat?: number | null;
  /** Location longitude. */
  lng?: number | null;
  /** Location name. */
  locationName?: string | null;
  /** Sets whether view mode control is enabled. */
  viewModeControl?: boolean | null;
  /** Initial zoom value. */
  initialZoom?: number | null;
  /** Map type. `HYBRID` is a combination of the `ROADMAP` and `SATELLITE` map types. */
  mapType?: MapTypeWithLiterals;
}

export enum MapType {
  /** Roadmap map type */
  ROADMAP = 'ROADMAP',
  /** Satellite map type */
  SATELITE = 'SATELITE',
  /** Hybrid map type */
  HYBRID = 'HYBRID',
  /** Terrain map type */
  TERRAIN = 'TERRAIN',
}

/** @enumType */
export type MapTypeWithLiterals =
  | MapType
  | 'ROADMAP'
  | 'SATELITE'
  | 'HYBRID'
  | 'TERRAIN';

export interface ParagraphData {
  /** Styling for the paragraph text. */
  textStyle?: TextStyle;
  /** Indentation level from 1-4. */
  indentation?: number | null;
  /** Paragraph level */
  level?: number | null;
}

export interface PollData {
  /** Styling for the poll's container. */
  containerData?: PluginContainerData;
  /** Poll data. */
  poll?: Poll;
  /** Layout settings for the poll and voting options. */
  layout?: PollDataLayout;
  /** Styling for the poll and voting options. */
  design?: Design;
}

export enum ViewRole {
  /** Only Poll creator can view the results */
  CREATOR = 'CREATOR',
  /** Anyone who voted can see the results */
  VOTERS = 'VOTERS',
  /** Anyone can see the results, even if one didn't vote */
  EVERYONE = 'EVERYONE',
}

/** @enumType */
export type ViewRoleWithLiterals = ViewRole | 'CREATOR' | 'VOTERS' | 'EVERYONE';

export enum VoteRole {
  /** Logged in member */
  SITE_MEMBERS = 'SITE_MEMBERS',
  /** Anyone */
  ALL = 'ALL',
}

/** @enumType */
export type VoteRoleWithLiterals = VoteRole | 'SITE_MEMBERS' | 'ALL';

export interface Permissions {
  /** Sets who can view the poll results. */
  view?: ViewRoleWithLiterals;
  /** Sets who can vote. */
  vote?: VoteRoleWithLiterals;
  /** Sets whether one voter can vote multiple times. Defaults to `false`. */
  allowMultipleVotes?: boolean | null;
}

export interface Option {
  /** Option ID. */
  id?: string | null;
  /** Option title. */
  title?: string | null;
  /** The image displayed with the option. */
  image?: Media;
}

export interface PollSettings {
  /** Permissions settings for voting. */
  permissions?: Permissions;
  /** Sets whether voters are displayed in the vote results. Defaults to `true`. */
  showVoters?: boolean | null;
  /** Sets whether the vote count is displayed. Defaults to `true`. */
  showVotesCount?: boolean | null;
}

export enum PollLayoutType {
  /** List */
  LIST = 'LIST',
  /** Grid */
  GRID = 'GRID',
}

/** @enumType */
export type PollLayoutTypeWithLiterals = PollLayoutType | 'LIST' | 'GRID';

export enum PollLayoutDirection {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type PollLayoutDirectionWithLiterals =
  | PollLayoutDirection
  | 'LTR'
  | 'RTL';

export interface PollLayout {
  /** The layout for displaying the voting options. */
  type?: PollLayoutTypeWithLiterals;
  /** The direction of the text displayed in the voting options. Text can be displayed either right-to-left or left-to-right. */
  direction?: PollLayoutDirectionWithLiterals;
  /** Sets whether to display the main poll image. Defaults to `false`. */
  enableImage?: boolean | null;
}

export interface OptionLayout {
  /** Sets whether to display option images. Defaults to `false`. */
  enableImage?: boolean | null;
}

export enum PollDesignBackgroundType {
  /** Color background type */
  COLOR = 'COLOR',
  /** Image background type */
  IMAGE = 'IMAGE',
  /** Gradiant background type */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type PollDesignBackgroundTypeWithLiterals =
  | PollDesignBackgroundType
  | 'COLOR'
  | 'IMAGE'
  | 'GRADIENT';

export interface BackgroundGradient {
  /** The gradient angle in degrees. */
  angle?: number | null;
  /**
   * The start color as a hexademical value.
   * @format COLOR_HEX
   */
  startColor?: string | null;
  /**
   * The end color as a hexademical value.
   * @format COLOR_HEX
   */
  lastColor?: string | null;
}

export interface PollDesignBackground
  extends PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
  /** Background type. For each option, include the relevant details. */
  type?: PollDesignBackgroundTypeWithLiterals;
}

/** @oneof */
export interface PollDesignBackgroundBackgroundOneOf {
  /**
   * The background color as a hexademical value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** An image to use for the background. */
  image?: Media;
  /** Details for a gradient background. */
  gradient?: BackgroundGradient;
}

export interface PollDesign {
  /** Background styling. */
  background?: PollDesignBackground;
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface OptionDesign {
  /** Border radius in pixels. */
  borderRadius?: number | null;
}

export interface Poll {
  /** Poll ID. */
  id?: string | null;
  /** Poll title. */
  title?: string | null;
  /** Poll creator ID. */
  creatorId?: string | null;
  /** Main poll image. */
  image?: Media;
  /** Voting options. */
  options?: Option[];
  /** The poll's permissions and display settings. */
  settings?: PollSettings;
}

export interface PollDataLayout {
  /** Poll layout settings. */
  poll?: PollLayout;
  /** Voting otpions layout settings. */
  options?: OptionLayout;
}

export interface Design {
  /** Styling for the poll. */
  poll?: PollDesign;
  /** Styling for voting options. */
  options?: OptionDesign;
}

export interface TextData {
  /** The text to apply decorations to. */
  text?: string;
  /** The decorations to apply. */
  decorations?: Decoration[];
}

/** Adds appearence changes to text */
export interface Decoration extends DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
  /** The type of decoration to apply. */
  type?: DecorationTypeWithLiterals;
}

/** @oneof */
export interface DecorationDataOneOf {
  /** Data for an anchor link decoration. */
  anchorData?: AnchorData;
  /** Data for a color decoration. */
  colorData?: ColorData;
  /** Data for an external link decoration. */
  linkData?: LinkData;
  /** Data for a mention decoration. */
  mentionData?: MentionData;
  /** Data for a font size decoration. */
  fontSizeData?: FontSizeData;
  /** Font weight for a bold decoration. */
  fontWeightValue?: number | null;
  /** Data for an italic decoration. Defaults to `true`. */
  italicData?: boolean | null;
  /** Data for an underline decoration. Defaults to `true`. */
  underlineData?: boolean | null;
  /** Data for a spoiler decoration. */
  spoilerData?: SpoilerData;
  /** Data for a strikethrough decoration. Defaults to `true`. */
  strikethroughData?: boolean | null;
  /** Data for a superscript decoration. Defaults to `true`. */
  superscriptData?: boolean | null;
  /** Data for a subscript decoration. Defaults to `true`. */
  subscriptData?: boolean | null;
  /** Data for a font family decoration. */
  fontFamilyData?: FontFamilyData;
}

export enum DecorationType {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
  UNDERLINE = 'UNDERLINE',
  SPOILER = 'SPOILER',
  ANCHOR = 'ANCHOR',
  MENTION = 'MENTION',
  LINK = 'LINK',
  COLOR = 'COLOR',
  FONT_SIZE = 'FONT_SIZE',
  EXTERNAL = 'EXTERNAL',
  STRIKETHROUGH = 'STRIKETHROUGH',
  SUPERSCRIPT = 'SUPERSCRIPT',
  SUBSCRIPT = 'SUBSCRIPT',
  FONT_FAMILY = 'FONT_FAMILY',
}

/** @enumType */
export type DecorationTypeWithLiterals =
  | DecorationType
  | 'BOLD'
  | 'ITALIC'
  | 'UNDERLINE'
  | 'SPOILER'
  | 'ANCHOR'
  | 'MENTION'
  | 'LINK'
  | 'COLOR'
  | 'FONT_SIZE'
  | 'EXTERNAL'
  | 'STRIKETHROUGH'
  | 'SUPERSCRIPT'
  | 'SUBSCRIPT'
  | 'FONT_FAMILY';

export interface AnchorData {
  /** The target node's ID. */
  anchor?: string;
}

export interface ColorData {
  /** The text's background color as a hexadecimal value. */
  background?: string | null;
  /** The text's foreground color as a hexadecimal value. */
  foreground?: string | null;
}

export interface LinkData {
  /** Link details. */
  link?: Link;
}

export interface MentionData {
  /** The mentioned user's name. */
  name?: string;
  /** The version of the user's name that appears after the `@` character in the mention. */
  slug?: string;
  /** Mentioned user's ID. */
  id?: string | null;
}

export interface FontSizeData {
  /** The units used for the font size. */
  unit?: FontTypeWithLiterals;
  /** Font size value. */
  value?: number | null;
}

export enum FontType {
  PX = 'PX',
  EM = 'EM',
}

/** @enumType */
export type FontTypeWithLiterals = FontType | 'PX' | 'EM';

export interface SpoilerData {
  /** Spoiler ID. */
  id?: string | null;
}

export interface FontFamilyData {
  /** @maxLength 1000 */
  value?: string | null;
}

export interface AppEmbedData extends AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
  /** The type of Wix App content being embedded. */
  type?: AppTypeWithLiterals;
  /** The ID of the embedded content. */
  itemId?: string | null;
  /** The name of the embedded content. */
  name?: string | null;
  /**
   * Deprecated: Use `image` instead.
   * @deprecated
   */
  imageSrc?: string | null;
  /** The URL for the embedded content. */
  url?: string | null;
  /** An image for the embedded content. */
  image?: Media;
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Whether to hide the title. */
  hideTitle?: boolean | null;
  /** Whether to hide the price. */
  hidePrice?: boolean | null;
  /** Whether to hide the description (Event and Booking). */
  hideDescription?: boolean | null;
  /** Whether to hide the date and time (Event). */
  hideDateTime?: boolean | null;
  /** Whether to hide the location (Event). */
  hideLocation?: boolean | null;
  /** Whether to hide the duration (Booking). */
  hideDuration?: boolean | null;
  /** Whether to hide the button. */
  hideButton?: boolean | null;
  /** Whether to hide the ribbon. */
  hideRibbon?: boolean | null;
  /** Button styling options. */
  buttonStyles?: ButtonStyles;
  /** Image styling options. */
  imageStyles?: ImageStyles;
  /** Ribbon styling options. */
  ribbonStyles?: RibbonStyles;
  /** Card styling options. */
  cardStyles?: CardStyles;
  /** Styling for the app embed's container. */
  containerData?: PluginContainerData;
  /** Pricing data for embedded Wix App content. */
  pricingData?: PricingData;
}

/** @oneof */
export interface AppEmbedDataAppDataOneOf {
  /** Data for embedded Wix Bookings content. */
  bookingData?: BookingData;
  /** Data for embedded Wix Events content. */
  eventData?: EventData;
}

export enum Position {
  /** Image positioned at the start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Image positioned at the end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
  /** Image positioned at the top */
  TOP = 'TOP',
}

/** @enumType */
export type PositionWithLiterals = Position | 'START' | 'END' | 'TOP';

export enum AspectRatio {
  /** 1:1 aspect ratio */
  SQUARE = 'SQUARE',
  /** 16:9 aspect ratio */
  RECTANGLE = 'RECTANGLE',
}

/** @enumType */
export type AspectRatioWithLiterals = AspectRatio | 'SQUARE' | 'RECTANGLE';

export enum Resizing {
  /** Fill the container, may crop the image */
  FILL = 'FILL',
  /** Fit the image within the container */
  FIT = 'FIT',
}

/** @enumType */
export type ResizingWithLiterals = Resizing | 'FILL' | 'FIT';

export enum Placement {
  /** Ribbon placed on the image */
  IMAGE = 'IMAGE',
  /** Ribbon placed on the product information */
  PRODUCT_INFO = 'PRODUCT_INFO',
}

/** @enumType */
export type PlacementWithLiterals = Placement | 'IMAGE' | 'PRODUCT_INFO';

export enum CardStylesType {
  /** Card with visible border and background */
  CONTAINED = 'CONTAINED',
  /** Card without visible border */
  FRAMELESS = 'FRAMELESS',
}

/** @enumType */
export type CardStylesTypeWithLiterals =
  | CardStylesType
  | 'CONTAINED'
  | 'FRAMELESS';

export enum Alignment {
  /** Content aligned to start (left in LTR layouts, right in RTL layouts) */
  START = 'START',
  /** Content centered */
  CENTER = 'CENTER',
  /** Content aligned to end (right in LTR layouts, left in RTL layouts) */
  END = 'END',
}

/** @enumType */
export type AlignmentWithLiterals = Alignment | 'START' | 'CENTER' | 'END';

export enum Layout {
  /** Elements stacked vertically */
  STACKED = 'STACKED',
  /** Elements arranged horizontally */
  SIDE_BY_SIDE = 'SIDE_BY_SIDE',
}

/** @enumType */
export type LayoutWithLiterals = Layout | 'STACKED' | 'SIDE_BY_SIDE';

export enum AppType {
  PRODUCT = 'PRODUCT',
  EVENT = 'EVENT',
  BOOKING = 'BOOKING',
}

/** @enumType */
export type AppTypeWithLiterals = AppType | 'PRODUCT' | 'EVENT' | 'BOOKING';

export interface BookingData {
  /** Booking duration in minutes. */
  durations?: string | null;
}

export interface EventData {
  /** Event schedule. */
  scheduling?: string | null;
  /** Event location. */
  location?: string | null;
}

export interface ButtonStyles {
  /** Text to display on the button. */
  buttonText?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Border color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  borderColorHover?: string | null;
  /**
   * Text color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  textColorHover?: string | null;
  /**
   * Background color as a hexadecimal value (hover state).
   * @format COLOR_HEX
   */
  backgroundColorHover?: string | null;
  /** Button size option, one of `SMALL`, `MEDIUM` or `LARGE`. Defaults to `MEDIUM`. */
  buttonSize?: string | null;
}

export interface ImageStyles {
  /** Whether to hide the image. */
  hideImage?: boolean | null;
  /** Position of image. Defaults to `START`. */
  imagePosition?: PositionWithLiterals;
  /** Aspect ratio for the image. Defaults to `SQUARE`. */
  aspectRatio?: AspectRatioWithLiterals;
  /** How the image should be resized. Defaults to `FILL`. */
  resizing?: ResizingWithLiterals;
  /**
   * Image border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Image border width in pixels. */
  borderWidth?: number | null;
  /** Image border radius in pixels. */
  borderRadius?: number | null;
}

export interface RibbonStyles {
  /** Text to display on the ribbon. */
  ribbonText?: string | null;
  /**
   * Ribbon background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Ribbon text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
  /**
   * Ribbon border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Ribbon border width in pixels. */
  borderWidth?: number | null;
  /** Ribbon border radius in pixels. */
  borderRadius?: number | null;
  /** Placement of the ribbon. Defaults to `IMAGE`. */
  ribbonPlacement?: PlacementWithLiterals;
}

export interface CardStyles {
  /**
   * Card background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
  /**
   * Card border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Card border width in pixels. */
  borderWidth?: number | null;
  /** Card border radius in pixels. */
  borderRadius?: number | null;
  /** Card type. Defaults to `CONTAINED`. */
  type?: CardStylesTypeWithLiterals;
  /** Content alignment. Defaults to `START`. */
  alignment?: AlignmentWithLiterals;
  /** Layout for title and price. Defaults to `STACKED`. */
  titlePriceLayout?: LayoutWithLiterals;
  /**
   * Title text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  titleColor?: string | null;
  /**
   * Text color as a hexadecimal value.
   * @format COLOR_HEX
   */
  textColor?: string | null;
}

export interface PricingData {
  /**
   * Minimum numeric price value as string (e.g., "10.99").
   * @decimalValue options { maxScale:2 }
   */
  valueFrom?: string | null;
  /**
   * Maximum numeric price value as string (e.g., "19.99").
   * @decimalValue options { maxScale:2 }
   */
  valueTo?: string | null;
  /**
   * Numeric price value as string after discount application (e.g., "15.99").
   * @decimalValue options { maxScale:2 }
   */
  discountedValue?: string | null;
  /**
   * Currency of the value in ISO 4217 format (e.g., "USD", "EUR").
   * @format CURRENCY
   */
  currency?: string | null;
  /**
   * Pricing plan ID.
   * @format GUID
   */
  pricingPlanId?: string | null;
}

export interface VideoData {
  /** Styling for the video's container. */
  containerData?: PluginContainerData;
  /** Video details. */
  video?: Media;
  /** Video thumbnail details. */
  thumbnail?: Media;
  /** Sets whether the video's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Video title. */
  title?: string | null;
  /** Video options. */
  options?: PlaybackOptions;
}

export interface PlaybackOptions {
  /** Sets whether the media will automatically start playing. */
  autoPlay?: boolean | null;
  /** Sets whether media's will be looped. */
  playInLoop?: boolean | null;
  /** Sets whether media's controls will be shown. */
  showControls?: boolean | null;
}

export interface EmbedData {
  /** Styling for the oEmbed node's container. */
  containerData?: PluginContainerData;
  /** An [oEmbed](https://www.oembed.com) object. */
  oembed?: Oembed;
  /** Origin asset source. */
  src?: string | null;
}

export interface Oembed {
  /** The resource type. */
  type?: string | null;
  /** The width of the resource specified in the `url` property in pixels. */
  width?: number | null;
  /** The height of the resource specified in the `url` property in pixels. */
  height?: number | null;
  /** Resource title. */
  title?: string | null;
  /** The source URL for the resource. */
  url?: string | null;
  /** HTML for embedding a video player. The HTML should have no padding or margins. */
  html?: string | null;
  /** The name of the author or owner of the resource. */
  authorName?: string | null;
  /** The URL for the author or owner of the resource. */
  authorUrl?: string | null;
  /** The name of the resource provider. */
  providerName?: string | null;
  /** The URL for the resource provider. */
  providerUrl?: string | null;
  /** The URL for a thumbnail image for the resource. If this property is defined, `thumbnailWidth` and `thumbnailHeight` must also be defined. */
  thumbnailUrl?: string | null;
  /** The width of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailHeight` must also be defined. */
  thumbnailWidth?: string | null;
  /** The height of the resource's thumbnail image. If this property is defined, `thumbnailUrl` and `thumbnailWidth`must also be defined. */
  thumbnailHeight?: string | null;
  /** The URL for an embedded viedo. */
  videoUrl?: string | null;
  /** The oEmbed version number.  This value must be `1.0`. */
  version?: string | null;
}

export interface CollapsibleListData {
  /** Styling for the collapsible list's container. */
  containerData?: PluginContainerData;
  /** If `true`, only one item can be expanded at a time. Defaults to `false`. */
  expandOnlyOne?: boolean | null;
  /** Sets which items are expanded when the page loads. */
  initialExpandedItems?: InitialExpandedItemsWithLiterals;
  /** The direction of the text in the list. Either left-to-right or right-to-left. */
  direction?: DirectionWithLiterals;
  /** If `true`, The collapsible item will appear in search results as an FAQ. */
  isQapageData?: boolean | null;
}

export enum InitialExpandedItems {
  /** First item will be expended initally */
  FIRST = 'FIRST',
  /** All items will expended initally */
  ALL = 'ALL',
  /** All items collapsed initally */
  NONE = 'NONE',
}

/** @enumType */
export type InitialExpandedItemsWithLiterals =
  | InitialExpandedItems
  | 'FIRST'
  | 'ALL'
  | 'NONE';

export enum Direction {
  /** Left-to-right */
  LTR = 'LTR',
  /** Right-to-left */
  RTL = 'RTL',
}

/** @enumType */
export type DirectionWithLiterals = Direction | 'LTR' | 'RTL';

export interface TableData {
  /** Styling for the table's container. */
  containerData?: PluginContainerData;
  /** The table's dimensions. */
  dimensions?: Dimensions;
  /**
   * Deprecated: Use `rowHeader` and `columnHeader` instead.
   * @deprecated
   */
  header?: boolean | null;
  /** Sets whether the table's first row is a header. Defaults to `false`. */
  rowHeader?: boolean | null;
  /** Sets whether the table's first column is a header. Defaults to `false`. */
  columnHeader?: boolean | null;
  /** The spacing between cells in pixels. Defaults to `0`. */
  cellSpacing?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left.
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Table's alternative text. */
  altText?: string | null;
}

export interface Dimensions {
  /** An array representing relative width of each column in relation to the other columns. */
  colsWidthRatio?: number[];
  /** An array representing the height of each row in pixels. */
  rowsHeight?: number[];
  /** An array representing the minimum width of each column in pixels. */
  colsMinWidth?: number[];
}

export interface TableCellData {
  /** Styling for the cell's background color and text alignment. */
  cellStyle?: CellStyle;
  /** The cell's border colors. */
  borderColors?: BorderColors;
  /** Defines how many columns the cell spans. Default: 1. */
  colspan?: number | null;
  /** Defines how many rows the cell spans. Default: 1. */
  rowspan?: number | null;
  /** The cell's border widths. */
  borderWidths?: BorderWidths;
}

export enum VerticalAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentWithLiterals =
  | VerticalAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export interface CellStyle {
  /** Vertical alignment for the cell's text. */
  verticalAlignment?: VerticalAlignmentWithLiterals;
  /**
   * Cell background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  backgroundColor?: string | null;
}

export interface BorderColors {
  /**
   * Left border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  left?: string | null;
  /**
   * Right border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  right?: string | null;
  /**
   * Top border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  top?: string | null;
  /**
   * Bottom border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  bottom?: string | null;
}

export interface BorderWidths {
  /** Left border width in pixels. */
  left?: number | null;
  /** Right border width in pixels. */
  right?: number | null;
  /** Top border width in pixels. */
  top?: number | null;
  /** Bottom border width in pixels. */
  bottom?: number | null;
}

/**
 * `NullValue` is a singleton enumeration to represent the null value for the
 * `Value` type union.
 *
 * The JSON representation for `NullValue` is JSON `null`.
 */
export enum NullValue {
  /** Null value. */
  NULL_VALUE = 'NULL_VALUE',
}

/** @enumType */
export type NullValueWithLiterals = NullValue | 'NULL_VALUE';

/**
 * `ListValue` is a wrapper around a repeated field of values.
 *
 * The JSON representation for `ListValue` is JSON array.
 */
export interface ListValue {
  /** Repeated field of dynamically typed values. */
  values?: any[];
}

export interface AudioData {
  /** Styling for the audio node's container. */
  containerData?: PluginContainerData;
  /** Audio file details. */
  audio?: Media;
  /** Sets whether the audio node's download button is disabled. Defaults to `false`. */
  disableDownload?: boolean | null;
  /** Cover image. */
  coverImage?: Media;
  /** Track name. */
  name?: string | null;
  /** Author name. */
  authorName?: string | null;
  /** An HTML version of the audio node. */
  html?: string | null;
}

export interface OrderedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
  /** List start number. */
  start?: number | null;
}

export interface BulletedListData {
  /** Indentation level from 0-4. */
  indentation?: number;
  /** Offset level from 0-4. */
  offset?: number | null;
}

export interface BlockquoteData {
  /** Indentation level from 1-4. */
  indentation?: number;
}

export interface CaptionData {
  textStyle?: TextStyle;
}

export interface LayoutData {
  /**
   * Deprecated: Use `background` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backgroundColor?: string | null;
  /** Background image. */
  backgroundImage?: LayoutDataBackgroundImage;
  /**
   * Border color as a hexadecimal value.
   * @format COLOR_HEX
   */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /**
   * Deprecated: Use `backdrop` instead.
   * @format COLOR_HEX
   * @deprecated
   */
  backdropColor?: string | null;
  /** Backdrop image. */
  backdropImage?: LayoutDataBackgroundImage;
  /** Backdrop top padding. */
  backdropPaddingTop?: number | null;
  /** Backdrop bottom padding */
  backdropPaddingBottom?: number | null;
  /** Horizontal and vertical gap between columns */
  gap?: number | null;
  /**
   * Padding in pixels for cells. Follows CSS order: top, right, bottom, left
   * @maxSize 4
   */
  cellPadding?: number[];
  /** Vertical alignment for the cell's items. */
  cellVerticalAlignment?: VerticalAlignmentAlignmentWithLiterals;
  /** Responsiveness behaviour of columns when responsiveness applies. Either stacks or wrappers. */
  responsivenessBehaviour?: ResponsivenessBehaviourWithLiterals;
  /** Size in pixels when responsiveness_behaviour applies */
  responsivenessBreakpoint?: number | null;
  /** Styling for the layout's container. */
  containerData?: PluginContainerData;
  /** Defines where selected design propertied applies to */
  designTarget?: DesignTargetWithLiterals;
  /** Banner configuration. When present, this layout is displayed as a banner. */
  banner?: Banner;
  /** Background styling (color or gradient). */
  background?: LayoutDataBackground;
  /** Backdrop styling (color or gradient). */
  backdrop?: Backdrop;
}

export enum ImageScalingScaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ImageScalingScalingWithLiterals =
  | ImageScalingScaling
  | 'AUTO'
  | 'CONTAIN'
  | 'COVER';

export enum ImagePosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionWithLiterals =
  | ImagePosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

export enum Origin {
  /** Banner originated from an image */
  IMAGE = 'IMAGE',
  /** Banner originated from a layout */
  LAYOUT = 'LAYOUT',
}

/** @enumType */
export type OriginWithLiterals = Origin | 'IMAGE' | 'LAYOUT';

/** Background type */
export enum LayoutDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type LayoutDataBackgroundTypeWithLiterals =
  | LayoutDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Backdrop type */
export enum BackdropType {
  /** Solid color backdrop */
  COLOR = 'COLOR',
  /** Gradient backdrop */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type BackdropTypeWithLiterals = BackdropType | 'COLOR' | 'GRADIENT';

export interface LayoutDataBackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ImageScalingScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionWithLiterals;
}

export enum VerticalAlignmentAlignment {
  /** Top alignment */
  TOP = 'TOP',
  /** Middle alignment */
  MIDDLE = 'MIDDLE',
  /** Bottom alignment */
  BOTTOM = 'BOTTOM',
}

/** @enumType */
export type VerticalAlignmentAlignmentWithLiterals =
  | VerticalAlignmentAlignment
  | 'TOP'
  | 'MIDDLE'
  | 'BOTTOM';

export enum ResponsivenessBehaviour {
  /** Stacking of columns */
  STACK = 'STACK',
  /** Wrapping of columns */
  WRAP = 'WRAP',
}

/** @enumType */
export type ResponsivenessBehaviourWithLiterals =
  | ResponsivenessBehaviour
  | 'STACK'
  | 'WRAP';

export enum DesignTarget {
  /** Design applied to layout */
  LAYOUT = 'LAYOUT',
  /** Design applied to cells */
  CELL = 'CELL',
}

/** @enumType */
export type DesignTargetWithLiterals = DesignTarget | 'LAYOUT' | 'CELL';

export interface Banner {
  /** Origin of the banner */
  origin?: OriginWithLiterals;
}

/** Background styling (color or gradient) */
export interface LayoutDataBackground {
  /** Background type. */
  type?: LayoutDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

/** Backdrop styling (color or gradient) */
export interface Backdrop {
  /** Backdrop type. */
  type?: BackdropTypeWithLiterals;
  /**
   * Backdrop color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface LayoutCellData {
  /** Size of the cell in 12 columns grid. */
  colSpan?: number | null;
}

export interface ShapeData {
  /** Styling for the shape's container. */
  containerData?: PluginContainerData;
  /** Shape file details. */
  shape?: Media;
  /** Styling for the shape. */
  styles?: ShapeDataStyles;
}

export interface ShapeDataStyles {
  /**
   * Shape fill color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Map of original color keys to their new color values. */
  colors?: Record<string, string>;
}

export interface CardData {
  /** Background styling (color or gradient). */
  background?: CardDataBackground;
  /** Background image. */
  backgroundImage?: BackgroundImage;
}

export enum Scaling {
  /** Auto image scaling */
  AUTO = 'AUTO',
  /** Contain image scaling */
  CONTAIN = 'CONTAIN',
  /** Cover image scaling */
  COVER = 'COVER',
}

/** @enumType */
export type ScalingWithLiterals = Scaling | 'AUTO' | 'CONTAIN' | 'COVER';

export enum ImagePositionPosition {
  /** Image positioned at the center */
  CENTER = 'CENTER',
  /** Image positioned on the left */
  CENTER_LEFT = 'CENTER_LEFT',
  /** Image positioned on the right */
  CENTER_RIGHT = 'CENTER_RIGHT',
  /** Image positioned at the center top */
  TOP = 'TOP',
  /** Image positioned at the top left */
  TOP_LEFT = 'TOP_LEFT',
  /** Image positioned at the top right */
  TOP_RIGHT = 'TOP_RIGHT',
  /** Image positioned at the center bottom */
  BOTTOM = 'BOTTOM',
  /** Image positioned at the bottom left */
  BOTTOM_LEFT = 'BOTTOM_LEFT',
  /** Image positioned at the bottom right */
  BOTTOM_RIGHT = 'BOTTOM_RIGHT',
}

/** @enumType */
export type ImagePositionPositionWithLiterals =
  | ImagePositionPosition
  | 'CENTER'
  | 'CENTER_LEFT'
  | 'CENTER_RIGHT'
  | 'TOP'
  | 'TOP_LEFT'
  | 'TOP_RIGHT'
  | 'BOTTOM'
  | 'BOTTOM_LEFT'
  | 'BOTTOM_RIGHT';

/** Background type */
export enum CardDataBackgroundType {
  /** Solid color background */
  COLOR = 'COLOR',
  /** Gradient background */
  GRADIENT = 'GRADIENT',
}

/** @enumType */
export type CardDataBackgroundTypeWithLiterals =
  | CardDataBackgroundType
  | 'COLOR'
  | 'GRADIENT';

/** Background styling (color or gradient) */
export interface CardDataBackground {
  /** Background type. */
  type?: CardDataBackgroundTypeWithLiterals;
  /**
   * Background color as a hexadecimal value.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Gradient configuration. */
  gradient?: Gradient;
}

export interface BackgroundImage {
  /** Background image. */
  media?: Media;
  /** Background image opacity. */
  opacity?: number | null;
  /** Background image scaling. */
  scaling?: ScalingWithLiterals;
  /** Position of background. Defaults to `CENTER`. */
  position?: ImagePositionPositionWithLiterals;
}

export interface TocData {
  /** Heading levels included in the table of contents. Default: [1, 2, 3, 4, 5, 6]. */
  includedHeadings?: number[];
  /** List style. Default: PLAIN. */
  listStyle?: ListStyleWithLiterals;
  /** Optional override for the font size in pixels. */
  fontSize?: number | null;
  /** Optional override for the vertical spacing between items in pixels. */
  itemSpacing?: number | null;
  /**
   * Optional override for the text color.
   * @format COLOR_HEX
   */
  color?: string | null;
  /** Indentation style. Default: NESTED. */
  indentation?: IndentationWithLiterals;
}

/** List style. */
export enum ListStyle {
  /** No markers (default) */
  PLAIN = 'PLAIN',
  /** Numbered list */
  NUMBERED = 'NUMBERED',
  /** Alphabetic letters */
  LETTERS = 'LETTERS',
  /** Roman numerals */
  ROMAN = 'ROMAN',
  /** Bulleted list */
  BULLETED = 'BULLETED',
  /** Alphabetical index */
  ALPHABETICAL_INDEX = 'ALPHABETICAL_INDEX',
  /** Alphabetical index (compact top-row only) */
  ALPHABETICAL_INDEX_COMPACT = 'ALPHABETICAL_INDEX_COMPACT',
}

/** @enumType */
export type ListStyleWithLiterals =
  | ListStyle
  | 'PLAIN'
  | 'NUMBERED'
  | 'LETTERS'
  | 'ROMAN'
  | 'BULLETED'
  | 'ALPHABETICAL_INDEX'
  | 'ALPHABETICAL_INDEX_COMPACT';

/** Indentation style. */
export enum Indentation {
  /** Sub-headings indented under parents (default) */
  NESTED = 'NESTED',
  /** All items at the same level */
  FLAT = 'FLAT',
}

/** @enumType */
export type IndentationWithLiterals = Indentation | 'NESTED' | 'FLAT';

/** Data for a smart block node. */
export interface SmartBlockData {
  /** The type of the smart block. */
  type?: SmartBlockDataTypeWithLiterals;
  /** Layout orientation. HORIZONTAL or VERTICAL. Optional for variants with fixed orientation. */
  orientation?: string | null;
  /** Column size controlling cells per row. */
  columnSize?: ColumnSizeWithLiterals;
  /** Border color (for SOLID_JOINED_BOXES variant). */
  borderColor?: string | null;
  /** Border width in pixels (for SOLID_JOINED_BOXES variant). */
  borderWidth?: number | null;
  /** Border radius in pixels (for SOLID_JOINED_BOXES variant). */
  borderRadius?: number | null;
}

/** Layout type of the smart block */
export enum SmartBlockDataType {
  /** Grid-based layouts with solid box items containing title, body, and icon/image. */
  SOLID_BOXES = 'SOLID_BOXES',
  /** Numbered boxes. */
  NUMBERED_BOXES = 'NUMBERED_BOXES',
  /** Statistics display with large numbers/values. */
  STATS = 'STATS',
  /** Statistics with circular visual elements. */
  CIRCLE_STATS = 'CIRCLE_STATS',
  /** Staggered/zigzag grid layout with alternating box positions. */
  SOLID_BOXES_ALTERNATING = 'SOLID_BOXES_ALTERNATING',
  /** Grid layout with boxes visually joined (no gaps, shared container border). */
  SOLID_JOINED_BOXES = 'SOLID_JOINED_BOXES',
  /** Transparent cells with only a left side line. */
  SIDE_LINE_TEXT = 'SIDE_LINE_TEXT',
  /** Transparent cells with only a top line. */
  TOP_LINE_TEXT = 'TOP_LINE_TEXT',
  /** Outlined boxes with a numbered/icon circle at the top. */
  OUTLINE_BOXES_WITH_TOP_CIRCLE = 'OUTLINE_BOXES_WITH_TOP_CIRCLE',
  /** Large icon bullets with text content. */
  BIG_BULLETS = 'BIG_BULLETS',
  /** Small dot bullets with text content. */
  SMALL_BULLETS = 'SMALL_BULLETS',
  /** Arrow icon bullets with text content. */
  ARROW_BULLETS = 'ARROW_BULLETS',
  /** Process steps with numbered/icon labels above a horizontal line. */
  PROCESS_STEPS = 'PROCESS_STEPS',
  /** Statistics with bar visual elements. */
  BAR_STATS = 'BAR_STATS',
}

/** @enumType */
export type SmartBlockDataTypeWithLiterals =
  | SmartBlockDataType
  | 'SOLID_BOXES'
  | 'NUMBERED_BOXES'
  | 'STATS'
  | 'CIRCLE_STATS'
  | 'SOLID_BOXES_ALTERNATING'
  | 'SOLID_JOINED_BOXES'
  | 'SIDE_LINE_TEXT'
  | 'TOP_LINE_TEXT'
  | 'OUTLINE_BOXES_WITH_TOP_CIRCLE'
  | 'BIG_BULLETS'
  | 'SMALL_BULLETS'
  | 'ARROW_BULLETS'
  | 'PROCESS_STEPS'
  | 'BAR_STATS';

/** Column size controlling how many cells appear per row. */
export enum ColumnSize {
  /** Up to 4 cells in a row. */
  SMALL = 'SMALL',
  /** Up to 3 cells in a row (default). */
  MEDIUM = 'MEDIUM',
  /** Up to 2 cells in a row. */
  LARGE = 'LARGE',
  /** 1 cell in a row. */
  EXTRA_LARGE = 'EXTRA_LARGE',
}

/** @enumType */
export type ColumnSizeWithLiterals =
  | ColumnSize
  | 'SMALL'
  | 'MEDIUM'
  | 'LARGE'
  | 'EXTRA_LARGE';

/** Data for a smart block cell node. */
export interface SmartBlockCellData {
  /** Optional label text for the cell (e.g., for stats variants). */
  label?: string | null;
  /** Shape file details. */
  shape?: Media;
  /** Border color of the cell. */
  borderColor?: string | null;
  /** Border width in pixels. */
  borderWidth?: number | null;
  /** Border radius in pixels. */
  borderRadius?: number | null;
  /** The type of the parent smart block (must match parent). */
  type?: SmartBlockDataTypeWithLiterals;
  /** Accent color for non-background variants (e.g., line, bullet, label color). */
  accentColor?: string | null;
  /** Background color for background-based variants (SOLID_BOXES, SOLID_BOXES_ALTERNATING, SOLID_JOINED_BOXES). */
  backgroundColor?: string | null;
  /** Shape fill color as a hexadecimal value. */
  shapeColor?: string | null;
}

export interface Metadata {
  /** Schema version. */
  version?: number;
  /**
   * When the object was created.
   * @readonly
   * @deprecated
   */
  createdTimestamp?: Date | null;
  /**
   * When the object was most recently updated.
   * @deprecated
   */
  updatedTimestamp?: Date | null;
  /** Object ID. */
  id?: string | null;
}

export interface DocumentStyle {
  /** Styling for H1 nodes. */
  headerOne?: TextNodeStyle;
  /** Styling for H2 nodes. */
  headerTwo?: TextNodeStyle;
  /** Styling for H3 nodes. */
  headerThree?: TextNodeStyle;
  /** Styling for H4 nodes. */
  headerFour?: TextNodeStyle;
  /** Styling for H5 nodes. */
  headerFive?: TextNodeStyle;
  /** Styling for H6 nodes. */
  headerSix?: TextNodeStyle;
  /** Styling for paragraph nodes. */
  paragraph?: TextNodeStyle;
  /** Styling for block quote nodes. */
  blockquote?: TextNodeStyle;
  /** Styling for code block nodes. */
  codeBlock?: TextNodeStyle;
}

export interface TextNodeStyle {
  /** The decorations to apply to the node. */
  decorations?: Decoration[];
  /** Padding and background color for the node. */
  nodeStyle?: NodeStyle;
  /** Line height for text in the node. */
  lineHeight?: string | null;
}

export interface BadgeList {
  /**
   * Events badges.
   * @maxSize 10
   */
  badges?: Badge[];
}

export interface Badge {
  /** Badge type. */
  type?: BadgeTypeTypeWithLiterals;
  /**
   * Badge text.
   * @maxLength 50
   */
  text?: string | null;
}

export enum BadgeTypeType {
  /** 1st priority badge type. */
  FIRST_PRIORITY = 'FIRST_PRIORITY',
  /** 2nd priority badge type. */
  SECOND_PRIORITY = 'SECOND_PRIORITY',
  /** 3rd priority badge type. */
  THIRD_PRIORITY = 'THIRD_PRIORITY',
}

/** @enumType */
export type BadgeTypeTypeWithLiterals =
  | BadgeTypeType
  | 'FIRST_PRIORITY'
  | 'SECOND_PRIORITY'
  | 'THIRD_PRIORITY';

export interface EventStarted {
  /** Event schedule configuration. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Whether at least 1 guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface EventEnded {
  /** Event end timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Whether at least 1 guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface EventReminder {
  /** Reminder timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: Location;
  /** Event schedule configuration. */
  dateAndTimeSettings?: DateAndTimeSettings;
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   */
  title?: string;
  /**
   * ID of the user who created the event.
   * @format GUID
   */
  userId?: string | null;
  /** Time until the start of event. By default, the reminder letter is triggered 1 day before the start of event. */
  startsIn?: TimeDuration;
  /** Whether at least 1 guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface EventPublished {
  /** Event publishing timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event status. */
  status?: StatusWithLiterals;
  /**
   * Event ID. Indicates the original event from which the current event was derived from.
   * Can be used to track the original event and add missing information.
   * @format GUID
   */
  derivedFromEventId?: string | null;
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   */
  title?: string | null;
  /**
   * Short description that appears under the event title.
   * @maxLength 500
   */
  shortDescription?: string | null;
  /** Event location. */
  location?: Location;
  /** Event date  and time settings. */
  dateAndTimeSettings?: DateAndTimeSettings;
  /** Event page URL components. */
  eventPageUrl?: PageUrl;
  /** RSVP or ticketing registration details. */
  registration?: Registration;
}

export interface EventCloned {
  /** Event creation timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /** Event location. */
  location?: Location;
  /** Event date and time settings. */
  dateAndTimeSettings?: DateAndTimeSettings;
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   */
  title?: string;
  /**
   * ID of the user who created the event.
   * @format GUID
   */
  userId?: string | null;
  /** Event status. */
  status?: StatusWithLiterals;
  /**
   * Instance ID. Indicates the original app instance from which current event was derived from.
   * @format GUID
   */
  derivedFromInstanceId?: string | null;
  /**
   * Event ID. Indicates the original event from which current event was derived from.
   * @format GUID
   */
  derivedFromEventId?: string | null;
  /**
   * Map of copied ticket definitions from the original event. <br>
   * Key represents ticket definition ID in the original event. <br>
   * Value represents ticket definition ID in the newly event.
   */
  ticketDefinitions?: Record<string, string>;
}

export interface EventCanceled {
  /** Event cancellation timestamp in ISO UTC format. */
  timestamp?: Date | null;
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string;
  /**
   * Event title.
   * @minLength 1
   * @maxLength 120
   */
  title?: string;
  /**
   * ID of the user who created the event.
   * @format GUID
   */
  userId?: string | null;
  /** Whether at least 1 guest is registered to the event with any attendance status. */
  hasGuests?: boolean | null;
}

export interface CreateEventRequest {
  /** Event data. */
  event: Event;
  /** Whether to create the event as a draft. */
  draft?: boolean;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  /** Returns `shortDescription`, `mainImage` and `calendarUrls`. */
  DETAILS = 'DETAILS',
  /** Returns `description`. */
  TEXTS = 'TEXTS',
  /** Returns `registration`. */
  REGISTRATION = 'REGISTRATION',
  /** Returns `eventPageUrl`. */
  URLS = 'URLS',
  /** Returns `form`. */
  FORM = 'FORM',
  /** Returns `summaries`. */
  DASHBOARD = 'DASHBOARD',
  /** Returns `feed`. */
  FEED = 'FEED',
  /** Returns `onlineConferencing.session`. */
  ONLINE_CONFERENCING_SESSION = 'ONLINE_CONFERENCING_SESSION',
  /** Returns `seoSettings`. */
  SEO_SETTINGS = 'SEO_SETTINGS',
  /** Returns `agendaSettings`. */
  AGENDA = 'AGENDA',
  /** Returns `categories`. */
  CATEGORIES = 'CATEGORIES',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'DETAILS'
  | 'TEXTS'
  | 'REGISTRATION'
  | 'URLS'
  | 'FORM'
  | 'DASHBOARD'
  | 'FEED'
  | 'ONLINE_CONFERENCING_SESSION'
  | 'SEO_SETTINGS'
  | 'AGENDA'
  | 'CATEGORIES';

export interface CreateEventResponse {
  /** Created event. */
  event?: Event;
}

export interface CloneEventRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /** Event info to change in the cloned event. */
  event?: Event;
  /** Whether to clone the event as a draft. */
  draft?: boolean;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CloneEventResponse {
  /** Cloned event. */
  event?: Event;
}

export interface CloneEventFromOtherSiteRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * Origin instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CloneEventFromOtherSiteResponse {
  /** Cloned event. */
  event?: Event;
}

export interface UpdateEventRequest {
  /** Event info to update. */
  event?: Event;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface UpdateEventResponse {
  /** Updated event. */
  event?: Event;
}

export interface PublishDraftEventRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface PublishDraftEventResponse {
  /** Published event. */
  event?: Event;
}

export interface CancelEventRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface CancelEventResponse {
  /** Canceled event. */
  event?: Event;
}

export interface BulkCancelEventsByFilterRequest {
  /** Filter. See [Query Events](https://dev.wix.com/docs/rest/business-solutions/events/events-v3/query-events) for a list of supported filters. */
  filter?: CommonQueryV2;
}

export interface CommonQueryV2 extends CommonQueryV2PagingMethodOneOf {
  /** Paging options. Can't be used together with `cursorPaging`. */
  paging?: CommonPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 20
   */
  sort?: CommonSorting[];
}

/** @oneof */
export interface CommonQueryV2PagingMethodOneOf {
  /** Paging options. Can't be used together with `cursorPaging`. */
  paging?: CommonPaging;
}

export interface CommonSorting {
  /**
   * Name of the field to sort by.
   * @maxLength 100
   */
  fieldName?: string;
  /**
   * Sort order.
   * Use `ASC` for ascending order or `DESC` for descending order.
   *
   * Default: `ASC`.
   */
  order?: CommonSortOrderWithLiterals;
}

export enum CommonSortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type CommonSortOrderWithLiterals = CommonSortOrder | 'ASC' | 'DESC';

export interface CommonPaging {
  /**
   * Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information.
   * @max 1000
   */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface BulkCancelEventsByFilterResponse {}

export interface DeleteEventRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string;
}

export interface DeleteEventResponse {
  /**
   * Deleted event ID.
   * @format GUID
   */
  eventId?: string;
}

export interface BulkDeleteEventsByFilterRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  filter?: CommonQueryV2;
}

export interface BulkDeleteEventsByFilterResponse {}

export interface QueryEventsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query?: CommonQueryV2;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
  /**
   * Whether draft events should be returned in the response. <br>
   * **Note:** This parameter requires the `WIX_EVENTS.READ_DRAFT_EVENTS` permission.
   */
  includeDrafts?: boolean;
}

export interface QueryEventsResponse {
  /** Metadata for the paginated results. */
  pagingMetadata?: CommonPagingMetadataV2;
  /** List of events. */
  events?: Event[];
}

export interface CommonPagingMetadataV2 {
  /** Number of items to return. See [Paging](https://dev.wix.com/api/rest/getting-started/sorting-and-paging#getting-started_sorting-and-paging_paging) for more information. */
  count?: number | null;
  /** Requested offset. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: CommonCursors;
}

export interface CommonCursors {
  /** Cursor pointing to the next page in the list of results. */
  next?: string | null;
  /** Cursor pointing to the previous page in the list of results. */
  prev?: string | null;
}

export interface ExportEventsRequest {
  /** Query options. See [API Query Language](https://dev.wix.com/docs/rest/articles/get-started/api-query-language) for more details. */
  query?: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object.
   *
   * Learn more about [filtering](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object.
   *
   * Learn more about [sorting](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#sorting).
   */
  sort?: Sorting[];
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and offset the number of items. */
  paging?: Paging;
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Maximum number of items to return in the results.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * Pass the relevant cursor token from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface ExportEventsResponse {
  /** Metadata for the paginated results. */
  pagingMetadata?: PagingMetadataV2;
  /** List of events. */
  events?: Event[];
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor string pointing to the next page in the list of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor pointing to the previous page in the list of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface CountEventsByStatusRequest {
  /** Filter object in the following format: <br/> `"filter" : { "fieldName1": "value1" }`. */
  filter?: Record<string, any> | null;
  /**
   * Parameters to count events by.
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
  /**
   * Whether draft events should be returned in the response. <br>
   * **Note:** This parameter requires the `WIX_EVENTS.READ_DRAFT_EVENTS` permission.
   */
  includeDrafts?: boolean;
}

export interface CountEventsByStatusResponse {
  /** Metadata for the paginated results. */
  pagingMetadata?: CommonPagingMetadataV2;
  /** Filter facets. */
  facets?: Record<string, FacetCounts>;
}

export interface FacetCounts {
  /** Facet counts aggregated per value */
  counts?: Record<string, number>;
}

export interface ListUserEventsRequest {
  /** Paging options to limit and skip the number of items. */
  paging?: CommonPaging;
  /**
   * Sort order, defaults to `"created:asc"`.
   * See [supported fields](https://dev.wix.com/api/rest/wix-events/wix-events/filter-and-sort#wix-events_wix-events_filter-and-sort_list-query-events).
   * @maxSize 20
   */
  sort?: CommonSorting[];
  /**
   * Event status.
   * @maxSize 20
   */
  status?: StatusWithLiterals[];
  /** Allows to filter events by user relation to the event among all wix sites. */
  userFilter?: UserFilter;
  /**
   * Filter facets to include in the response.
   * See [supported facets](https://dev.wix.com/api/rest/wix-events/wix-events/filter-and-sort#wix-events_wix-events_filter-and-sort_list-query-events).
   * @maxLength 100
   * @maxSize 20
   */
  facet?: string[];
}

export interface UserFilter {
  /**
   * User who is related to the event.
   * @format GUID
   */
  userId?: string;
  /**
   * Relation of user to the event.
   * @minSize 1
   * @maxSize 20
   */
  relation?: RelationWithLiterals[];
}

export enum Relation {
  /** User is attending the event. The user RSVP'd with status of `YES` or has ordered tickets. */
  ATTENDING = 'ATTENDING',
}

/** @enumType */
export type RelationWithLiterals = Relation | 'ATTENDING';

export interface ListUserEventsResponse {
  /** Metadata for the paginated results. */
  pagingMetadata?: CommonPagingMetadataV2;
  /** List of events. */
  events?: Event[];
  /** Filter facets. */
  facets?: Record<string, FacetCounts>;
}

export interface ListEventsByCategoryRequest {
  /**
   * Category ID.
   * @format GUID
   */
  categoryId: string;
  /** Paging options to limit and skip the number of items. */
  paging?: CommonPaging;
  /**
   * Predefined sets of fields to return. If both `fields` and `fieldsets` are sent in the request, the union of both lists is returned.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface ListEventsByCategoryResponse {
  /** Metadata for the paginated results. */
  pagingMetadata?: CommonPagingMetadataV2;
  /** List of events. */
  events?: Event[];
}

export interface GetEventRequest {
  /**
   * Event ID.
   * @format GUID
   */
  eventId: string | null;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

export interface GetEventResponse {
  /** Event. */
  event?: Event;
}

export interface GetEventBySlugRequest {
  /**
   * URL slug.
   * @minLength 1
   * @maxLength 130
   */
  slug: string | null;
  /** @maxSize 20 */
  fields?: RequestedFieldsWithLiterals[];
}

export interface GetEventBySlugResponse {
  /** Event. */
  event?: Event;
}

export interface FindEventRequest extends FindEventRequestFindByOneOf {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * URL slug.
   * @minLength 1
   * @maxLength 130
   */
  slug?: string | null;
  /**
   * Predefined sets of fields to return.
   * @maxSize 20
   */
  fields?: RequestedFieldsWithLiterals[];
}

/** @oneof */
export interface FindEventRequestFindByOneOf {
  /**
   * Event ID.
   * @format GUID
   */
  eventId?: string | null;
  /**
   * URL slug.
   * @minLength 1
   * @maxLength 130
   */
  slug?: string | null;
}

export interface FindEventResponse {
  /** Event. */
  event?: Event;
}

export interface Empty {}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entityAsJson?: string;
  /** Indicates the event was triggered by a restore-from-trashbin operation for a previously deleted entity */
  restoreInfo?: RestoreInfo;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntityAsJson?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntityAsJson?: string | null;
}

export interface ActionEvent {
  bodyAsJson?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/** @docsIgnore */
export type CreateEventApplicationErrors =
  | {
      code?: 'INVALID_SLUG_FORMAT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'EVENT_LIMIT_REACHED';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SLUG_ALREADY_IN_USE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CreateEventValidationErrors = {
  ruleName?: 'INVALID_EVENT_CONFIGURATION';
};
/** @docsIgnore */
export type UpdateEventApplicationErrors =
  | {
      code?: 'INVALID_EVENT_STATUS_TRANSITION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'INVALID_SLUG_FORMAT';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'SLUG_ALREADY_IN_USE';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type UpdateEventValidationErrors = {
  ruleName?: 'INVALID_EVENT_CONFIGURATION';
};
/** @docsIgnore */
export type PublishDraftEventApplicationErrors =
  | {
      code?: 'INVALID_EVENT_STATUS_TRANSITION';
      description?: string;
      data?: Record<string, any>;
    }
  | {
      code?: 'EVENT_ALREADY_PUBLISHED';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type CancelEventApplicationErrors = {
  code?: 'INVALID_EVENT_STATUS_TRANSITION';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type QueryEventsApplicationErrors = {
  code?: 'FILTER_PARSER_ERROR';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type CountEventsByStatusApplicationErrors = {
  code?: 'FILTER_PARSER_ERROR';
  description?: string;
  data?: Record<string, any>;
};
